<script setup lang="ts">
/* eslint-disable @typescript-eslint/naming-convention, @typescript-eslint/no-explicit-any */
import { computed, onBeforeUnmount, onMounted, ref, type Ref, watch } from 'vue';

import { useAsyncWabunkaExperience } from '@/composables/models/wabunka/useAsyncWabunkaExperience';
import { useAsyncWabunkaExperiencesSearch } from '@/composables/models/wabunka/useAsyncWabunkaExperiencesSearch';
import { useBuildUrl } from '@/composables/utils/useBuildUrl';
import { useLpTracking } from '@/lp/experiential-stay/composables/useLpTracking';
import { OptimizedImage } from '@/wabunka-src/common/components';
import { useOptimizedImageUrl } from '@/wabunka-src/common/composables/general/useOptimizedImageUrl';
import { useMicroCMSGetList } from '@/wabunka-src/common/composables/query/useMicroCMSClient';
import { CURATED_STAYS_THEME_SLUG } from '@/wabunka-src/constants/navigation';
import { ExperienceCard } from '@/wabunka-src/features/Experience';
import { useGetExperienceSectionsQuery } from '@/wabunka-src/features/Experience/composables/query/useGetExperienceSectionsQuery';
import { displayPrice } from '@/wabunka-src/features/Experience/models/experience';
import { type ExperienceSectionListViewModel } from '@/wabunka-src/features/Experience/models/experienceSection';
import { displayDestinationTagName } from '@/wabunka-src/features/Experience/models/wabunkaExperienceTag';
import { buildExperientialStayDuration } from '@/wabunka-src/features/Top/models/experientialStay';

definePageMeta({ headerBgTransparent: true, lp: { indexable: true } });

const { buildUrlWithEndSlash } = useBuildUrl();
useHead({
  bodyAttrs: { class: 'curated-stay-page' },
});

const getOptimizedImageUrl = useOptimizedImageUrl();

const { trackHeroCta, trackItineraryCta, trackTab, trackDestination, trackTheme, trackArticle, trackStory, trackGalleryViewMore } = useLpTracking();

// SPA 再訪時にリスナー・タイマー・Observer が多重登録されるのを防ぐため、mount 時に登録した teardown をここに集約する。
const cleanupFns: (() => void)[] = [];

// body クラスはページ離脱時に明示除去して CSS リークを防ぐ。
onBeforeUnmount(() => {
  cleanupFns.forEach((fn) => fn());
  cleanupFns.length = 0;
  if (import.meta.client) document.body.classList.remove('curated-stay-page');
});

useSeoMeta({
  title: 'Experiential Stay | Wabunka',
  description: "Some of Japan's most rewarding discoveries begin after check-in.",
  ogTitle: 'Experiential Stay',
  ogDescription: "Some of Japan's most rewarding discoveries begin after check-in.",
  ogImage: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/667146236f374bac9968a1fa36a3f79f/OGP.png',
  ogUrl: buildUrlWithEndSlash('/lp/experiential-stay'),
  twitterCard: 'summary_large_image',
  twitterTitle: 'Experiential Stay',
  twitterDescription: "Some of Japan's most rewarding discoveries begin after check-in.",
  twitterImage: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/667146236f374bac9968a1fa36a3f79f/OGP.png',
});

const galleryItems = [
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d2270576ca8f42ad870be246b6cd9356/TKE_4934_retouch2.jpg',
    alt: 'Sumiya Ryokan',
    link: '/experiences/en_sumiyaryokan',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/de9b5fdfc25d440f8486e9433a733bad/en_roka_SL_22.jpg',
    alt: 'Roka',
    link: '/experiences/en_roka',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/858d67ae5d644ebf83fda20a8f92d080/en_makinooto_SNa.jpg',
    alt: 'Makinooto Geisha',
    link: '/experiences/en_makinooto-geisha',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/84484f2b0d824e07a83f3b73020467e0/OTONAMI%E8%AE%92%E3%83%BB_M4A2907.jpg',
    alt: 'Kitano Hotel Koikeshuko',
    link: '/itineraries/kitanohotel-koikeshuko',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d577a12fc7614d45b810393062cacd0e/en_fujiseiran_SL04.jpg',
    alt: 'Fujiseiran',
    link: '/experiences/en_fujiseiran',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/99950d20f1694170afa551d9b5f2af4e/ochiairo%20(1).jpg',
    alt: 'Ochiairo',
    link: '/experiences/en_ochiairo',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/19d3ddb9f6e34062a7f5249e9a402127/en_fushiSN.jpg',
    alt: 'Fushi',
    link: '/experiences/en_fushi/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/e574fb8e8f6e40e88d9f855514b77518/en_higashiyama-shikikaboku_SL_13-C.jpg',
    alt: 'Higashiyama Shikikaboku',
    link: '/experiences/en_higashiyama-shikikaboku',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/c56697f5c782465996eda5ee28c28b91/%E7%BE%8E%E5%B1%B1F%26B_%E5%86%85%E8%A6%B3A01%EF%BD%9CA11_IGP8125.jpg',
    alt: 'Miyama Honkan',
    link: '/itineraries/miyama-honkan',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/85995e72e83a44ae91b71cfcf11ee77b/en_hoshinooto_AR_22.png',
    alt: 'Hoshinooto',
    link: '/experiences/en_hoshinooto',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/79302fda054547e7910db0f4865e5ec4/en_byaku_SL_01.jpg',
    alt: 'Byaku',
    link: '/experiences/en_byaku',
  },
];

const galleryModalExtraItems = [
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/072c5b0db98b45868ce5e7dfc7f75aba/en_roka_AR_02.jpg',
    alt: 'Roka',
    link: '/experiences/en_roka',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/f5790c5b60bb470f8b1d2b5d080b04c1/en_kitanohotel_1.jpg',
    alt: 'Kitano Hotel',
    link: '/itineraries/kitanohotel',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/71bf9fe84c2247d38406e6568f16f253/en_fujiseiran_SL07.jpg',
    alt: 'Fujiseiran',
    link: '/experiences/en_fujiseiran',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d8d72d981a7a4eeb9dd18e06e4fff530/en_fujiseiran_SL01.jpg',
    alt: 'Fujiseiran',
    link: '/experiences/en_fujiseiran',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9b7be59aac1943ae8f840ebd2dd7c951/en_makinooto-shuzo_SL_01_new2.jpg',
    alt: 'Makinooto Shuzo',
    link: '/experiences/en_makinooto-shuzo',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/41b38affd4454c1eb6f58fd5c5fae2f6/en_keyaki-en_AR_06.jpg',
    alt: 'Keyaki-en',
    link: '/experiences/en_keyaki-en',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/e3cb45b6927e407bb752b34391d7c8e8/en_hiiragiya_AR01.jpg',
    alt: 'Hiiragiya',
    link: '/experiences/en_hiiragiya',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/230c3a4e0efd42aea6c8c17462fc154c/en_meitokuan_AR13.png',
    alt: 'Meitokuan',
    link: '/experiences/en_meitokuan',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d0bf94a63d41473bbd80d2b883d40313/en_takigahara-natadera_SL_04_new.jpg',
    alt: 'Takigahara Natadera',
    link: '/experiences/en_takigahara-natadera/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/407855e374224175aabd7a0077968ecb/taniya-houkokusha_AR3.jpg',
    alt: 'Taniya Culture',
    link: '/experiences/en_taniya-culture/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/f649719553ea4d4c87ea1be223f83888/en_ryokankurashiki-basic_SN.jpg',
    alt: 'Ryokan Kurashiki',
    link: '/experiences/en_ryokankurashiki-basic',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/57156f92f2184626905325d64549e345/en_shinsho-an_SL4.jpg',
    alt: 'Shinsho-an',
    link: '/experiences/en_shinsho-an',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/c010752e40264dadbb632cecfb702bd6/en_hiramatsukyoto_AR%20(1).jpg',
    alt: 'Hiramatsu Kyoto',
    link: '/experiences/en_hiramatsukyoto-tea/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d2f5cb91404b4ec485fe0776c498706a/en_hiramatsukyoto.png',
    alt: 'Hiramatsu Kyoto',
    link: '/experiences/en_hiramatsukyoto-tea/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9fdb110af691499d932db5c014553410/notori-thousandth-trekking_AR12.jpg',
    alt: 'Notori Thousandth Trekking Tour',
    link: '/experiences/en_notori-thousandth-trekkingtour',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/bbc64980e2b04d82ad8e7aa532e0cd24/en_miyama-hotaruan.jpeg',
    alt: 'Miyama Hotaruan',
    link: '/itineraries/miyama-honkan',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/8f9f0987cda94d1eb3c025d8f966c4a1/en_amz-kyoto_780-520_017.jpg',
    alt: 'Aman Kyoto',
    link: '/experiences/en_amz-kyoto',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9f39bc4f838b44a8ab943b7c36f99791/amz-kyoto_780-520_011.jpeg',
    alt: 'Aman Kyoto',
    link: '/experiences/en_amz-kyoto',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/19d3ddb9f6e34062a7f5249e9a402127/en_fushiSN.jpg',
    alt: 'Fushi',
    link: '/experiences/en_fushi/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1d5444ecd783407f869d4d6da3f27403/en_fushi_SL16.jpg',
    alt: 'Fushi',
    link: '/experiences/en_fushi/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/b63decd211eb4c1991691ca224059945/en_mitoreya_AR%20(1).jpg',
    alt: 'Mitoreya',
    link: '/experiences/en_mitoreya',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/405f97a35d004b5085070a9965bdba36/en_mitoreya_SL%20(10).jpg',
    alt: 'Mitoreya',
    link: '/experiences/en_mitoreya',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/6122ba439bcf44a7a59a4dad45e8656d/en_hanaougi_AR8.jpg',
    alt: 'Hanaougi',
    link: '/experiences/en_hanaougi',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/bb08a9b94f194a0abc6f2e01221eb6b0/en_hanaougi_SN%20.jpg',
    alt: 'Hanaougi',
    link: '/experiences/en_hanaougi',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/ac4c9abb242a427db211c43ed5cf0297/en_kuon_SL11.jpg',
    alt: 'Kuon',
    link: '/experiences/en_kuon',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/4f744fe8ae234e72ac5f72cc0de8d257/en_kuon_AR10.jpg',
    alt: 'Kuon',
    link: '/experiences/en_kuon',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/b23f37f9f80b4010b03c7cfd669478b1/o-nishiyama-okamotoshoyu-shinshoji_AR06.jpg',
    alt: 'Onomichi 3 Days',
    link: '/itineraries/onomichi-3days',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/6bc40e97150b4b00b23f6fa73db0ab3d/hanamurasaki-yukamabune_AR14.jpg',
    alt: 'Hanamurasaki Yukamabune',
    link: '/itineraries/hanamurasaki-yukamabune',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/807453b6f3cb4b938d56f3d6ad88a798/hanamurasaki-yukamabune_AR15.jpg',
    alt: 'Hanamurasaki Yukamabune',
    link: '/itineraries/hanamurasaki-yukamabune',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/5a62d3efe26a451db336f1868fe8704c/en_simose_SL_19.jpg',
    alt: 'Simose',
    link: '/experiences/en_simose/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/a7e60ea1554948a0b725e75329d4baac/en_simose_SL_02.jpg',
    alt: 'Simose',
    link: '/experiences/en_simose/',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/4ef5b15da59241a3acf143be83508875/en_wataya_AR01.jpg',
    alt: 'Wataya',
    link: '/itineraries/wataya',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/27666dcf03a34f5885997bfd5d3837db/en_wataya_AR09.png',
    alt: 'Wataya',
    link: '/itineraries/wataya',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/94cc78d7da524025a9f99c5da89d146f/agriturismo-yutaka_AR%20(10).jpg',
    alt: 'Agriturismo Yutaka',
    link: '/itineraries/agriturismo-yutaka',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/8bb3f2e5bdd24297b6c75014f290b53c/agriturismo-yutaka_AR%20(1).jpg',
    alt: 'Agriturismo Yutaka',
    link: '/itineraries/agriturismo-yutaka',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/a8242cceef384a6a9016d2ed084f8553/en_eaugeu-basic_SN.jpg',
    alt: 'Eaugeu',
    link: '/experiences/en_eaugeu-basic',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/a4b643d8dd7d4cc0ad9a38ac1f4ddd9b/en_eaufeu-basic_SL.jpg7.jpg',
    alt: 'Eaufeu',
    link: '/itineraries/eaufeu-noguchinaohiko',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/e78618e99ce14534afd15226f392d181/en_matsuzakaya-stay_AR04.jpg',
    alt: 'Matsuzakaya Stay',
    link: '/experiences/en_matsuzakaya-stay',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/e3a26f09d7594643802812f9470f5828/en_seasion-stay_SL%20(3).jpg',
    alt: 'Seasion Stay',
    link: '/experiences/en_seasion-stay',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d366a8911ee54781b90513589d5500d4/en_suehiro_from_slug.jpg',
    alt: 'Suehiro',
    link: '/experiences/en_suehiro',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/33d58aadfd8b4f05b9b9c9281d24045c/en_madokanomori-stay_AR08.jpg',
    alt: 'Madoka no Mori Stay',
    link: '/experiences/en_madokanomori-stay',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/52654eb8617b43b686d12dea7ace6b1b/en_madokanomori-stay_AR02.jpg',
    alt: 'Madoka no Mori Stay',
    link: '/experiences/en_madokanomori-stay',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9237973b1b9644acb2e1bc501afca90f/en_normao_AR05.jpg',
    alt: 'Normao',
    link: '/experiences/en_normao',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/8fa5a84b56524905aae6d13a6a0ad94e/kinnotake-tonosawa_AR02.jpg',
    alt: 'Kinnotake Tonosawa',
    link: '/itineraries/kinnotake-tonosawa',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/f2968e15b20446928fd5db3d27c310a0/ryokankurashiki_AR.jpg',
    alt: 'Ryokan Kurashiki',
    link: '/itineraries/ryokankurashiki-seasion',
  },
  {
    src: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3f00b0e9d59044d3a8e200d869984f18/busuitei_AR%20(15).jpg',
    alt: 'Busuitei',
    link: '/itineraries/busuitei',
  },
];

// モーダルはスライダーの全画像に追加分を足して表示する。
const galleryModalItems = [...galleryItems, ...galleryModalExtraItems];

const AREA_INFO = {
  hokkaido: {
    region: 'Hokkaido',
    title: 'Hokkaido',
    desc: "Japan's vast northern frontier, where untamed wilderness meets quiet refinement. A land shaped by long winters and extraordinary nature — offering a rare stillness that invites genuine restoration.",
    tags: ['Sapporo', 'Niseko', 'Furano'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/909ed712f1444a9cbc7da1eaef75db5e/itinerary-long13_PCKV01.jpg',
  },
  tohoku: {
    region: 'Tohoku Region',
    title: 'Northern Japan',
    desc: `A region of deep forests, ancient hot springs, and sacred mountains still untouched by mass tourism. Tohoku preserves a raw, unhurried version of Japan — where tradition endures not as performance, but as everyday life.`,
    tags: ['Aomori', 'Hirosaki', 'Towada'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/7c663e361e5540388912ab9c8c4244cc/northern%20japan.jpg',
    objectPosition: 'center top',
  },
  tokyo: {
    region: 'Kanto Region',
    title: 'Tokyo & Around',
    desc: `Beyond the luminous skyline of Tokyo lies a quieter Japan — mountain villages, sacred trails, and centuries-old sake breweries. A rare duality: world-class urban culture alongside profound rural solitude, all within reach.`,
    tags: ['Tokyo', 'Kamakura', 'Okutama', 'Other places'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/316d937bf7114012add1a6d51b2319dd/AdobeStock_704320852.jpeg',
  },
  'mt-fuji-hakone': {
    region: 'Chubu Region',
    title: 'Mt. Fuji, Hakone & Around',
    desc: `From the iconic silhouette of Fuji to the lakeside ryokan of Hakone, this region offers the quintessential Japanese landscape. Private onsen villas overlook ancient crater lakes; mountain retreats serve kaiseki composed from local harvest.`,
    tags: ['Mt. Fuji & Around', 'Hakone', 'Numazu'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/abcd1b6fa84d4d8d8b78eafc331b61c9/fujijpg.jpg',
    objectPosition: 'center 20%',
  },
  'gifu-nagano': {
    region: 'Chubu Region',
    title: 'Gifu, Nagano & Around',
    desc: `From the lacquerware ateliers of Hida Takayama to the alpine valleys of Matsumoto, this is a region shaped by extraordinary craft and mountain solitude. Ancient post towns and family-owned ryokan preserve a Japan largely untouched by modernity.`,
    tags: ['Hida Takayama', 'Matsumoto', 'Kiso', 'Nagoya'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/f8670d2529c9412784ae6402f1b58aae/gifu_around.jpg',
  },
  kanazawa: {
    region: 'Hokuriku Region',
    title: 'Kanazawa & Around',
    desc: `Japan's cultural capital of the Sea of Japan coast. Kanazawa's immaculate samurai districts, traditional craft culture, and pristine local cuisine draw those who seek depth over spectacle — a city where time moves at a considered pace.`,
    tags: ['Kanazawa', 'Niigata', 'Toyama', 'Fukui'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/8677b4a205d94e379980879a9b6f30b9/kanazawa.jpg',
  },
  kansai: {
    region: 'Kansai Region',
    title: 'Kyoto, Osaka & Around',
    desc: `The spiritual and aesthetic heart of Japan. Kyoto's machiya townhouses and centuries-old ryokan offer an intimacy with tradition rarely found elsewhere — where a single tea ceremony or an evening with a geisha can quietly reframe how you understand beauty.`,
    tags: ['Kyoto', 'Osaka', 'Nara', 'Other places'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/360c7854cd5e48b7a23b5859d3ee8cec/PC_kyoto.jpg',
    objectPosition: 'center 40%',
  },
  chugoku_shikoku: {
    region: 'Chugoku & Shikoku',
    title: 'Hiroshima & the Inland Sea',
    desc: `A constellation of islands, historic merchant towns, and world-class contemporary art. The Seto Inland Sea rewards the unhurried traveller — with private villas by architectural masters and ryokan that have stood for generations.`,
    tags: ['Hiroshima', 'Onomichi', 'Kurashiki', 'Naoshima & Islands'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/aed3dd355e7a471eb660c573c9096f6b/hiroshima_inland_sea.jpg',
    objectPosition: 'center 70%',
  },
  kyushu: {
    region: 'Kyushu',
    title: 'Kyushu',
    desc: `Japan's southernmost main island pulses with volcanic energy and ancient craftsmanship. From the ceramic villages of Arita to the sacred sites of Nagasaki, Kyushu holds a distinct cultural identity — warmer, wilder, and deeply its own.`,
    tags: ['Fukuoka', 'Nagasaki', 'Kumamoto', 'Kagoshima'],
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/ecf6469354d94fa79c041647e1ffc89f/wataya_PCKV%20(8).jpg',
  },
};

const MAP_STAYS = {
  hokkaido: [],
  tohoku: [
    {
      name: '[Multi-Day Journey] Aomori, Hirosaki, and Towada: A Northern Symphony of Excitement and Stillness',
      location: 'Aomori · Hirosaki · Towada',
      url: '/itineraries/long_aomori',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/909ed712f1444a9cbc7da1eaef75db5e/itinerary-long13_PCKV01.jpg',
    },
  ],
  tokyo: [
    {
      name: 'Oyashiki Stay — Shodoshima Private Villa',
      location: 'Tokyo',
      url: '/experiences/en_oyashikistay/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1bd620677736489389d6781858ea9687/en_oyashikistay_PCKV.jpg',
    },
    {
      name: 'Luxury Central Tokyo Stay — Marunouchi City Tour',
      location: 'Tokyo',
      url: '/itineraries/kitanohotel-koikeshuko',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3fb69a9fc90c4226928f94ca83306297/en_oyashikistay_AR04.jpg',
    },
    {
      name: 'Explore Sacred Mt. Mitake — Private Riverside Villa',
      location: 'Okutama',
      url: '/itineraries/mt-tokyo-fushi',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3647aae10fa04efe9ac5dcf1c2fe73a6/en_oyashikistay_AR05.jpg',
    },
    {
      name: 'Unwind in Okutama — Tour a 300-Year Old Sake Brewery',
      location: 'Okutama',
      url: '/itineraries/satologue-ozawashuzo',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/fd5185b67587490d887a69ad3efd4a86/en_oyashikistay_PCCA01.jpg',
    },
    {
      name: 'Matsuzakaya Stay — Historic Machiya in Tokyo',
      location: 'Tokyo',
      url: '/experiences/en_matsuzakaya-stay/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/32c738cbfde84a3aa3087e0aeeb552be/images_en_ochiairo_sub_20_51525c7b.jpg',
    },
    {
      name: 'Miwa Yugawara — Hot Spring & Mountain Retreat',
      location: 'Kanagawa',
      url: '/experiences/en_miwayugawara/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/c35751c4c5fc48b3b0bd08d8e551129d/en_fujiseiran_PCKV.jpg',
    },
    {
      name: 'Fujiya Hotel — Explore the Old Tokaido Road',
      location: 'Hakone',
      url: '/itineraries/fujiya-explorehakone',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
    {
      name: 'Fuji Seiran — Luxury Stay in Hakone & Numazu',
      location: 'Hakone · Numazu',
      url: '/itineraries/fujiseiran-ashinoko-numazu',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/eb2b054a729b41d1955e4259531a447e/images_3355d8a4-508d-4e54-a644-74ea2bc35a4c.jpeg',
    },
    {
      name: 'Ochiairo Ryokan — A Valley Retreat of Rare Intimacy',
      location: 'Izu, Shizuoka',
      url: '/experiences/en_ochiairo/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/d3ad58da45a0456aaafe8c0fe650ca89/images_6950af3c-239b-4e88-9f60-6d27867c77ca.jpeg',
    },
    {
      name: 'Private Onsen with a View of Mt. Fuji',
      location: 'Shizuoka',
      url: '/s/experiences',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/a21f28bb6c4c47a893b5745b1f03b8cc/images_6faac036-f236-4b4b-b543-1f62ea9bee8a.jpeg',
    },
    {
      name: 'Lounge with Mt. Fuji — Refined Day Retreat',
      location: 'Hakone',
      url: '/s/experiences',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/bbfede99052e45cb9be7df8115329a50/images_d704a0a1-b6ab-410b-b11c-59ec5b1e991e.jpeg',
    },
    {
      name: 'Seaside Infinity Bath — Coastal Wellness',
      location: 'Shizuoka',
      url: '/s/experiences',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/24dbfa7e263148469098adde5f0cb3d7/en_meitokuan_AR05.jpg',
    },
    {
      name: 'Forest Spa — Architecture in Nature',
      location: 'Hakone',
      url: '/s/experiences',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/f64fb0b0c66140a8a938b24d911f432a/en_takigahara-natadera_SN6.jpg',
    },
    {
      name: 'Hana Ougi — Elegant City Sanctuary',
      location: 'Tokyo',
      url: '/experiences/en_hanaoug/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/f9b0f66d8d89410eba41e55dc67cb231/miyama_PCKV%20(1).jpg',
    },
    {
      name: 'Riverside Villa — Sacred Mt. Mitake Journey',
      location: 'Okutama',
      url: '/itineraries/mt-tokyo-fushi',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/85a0533609964ee9afd09a85b5b54536/images_en_yoshida-sanso_sub_6_c1af9ffb%20(1).jpg',
    },
    {
      name: 'Satologue — Forest Stay & Sake Culture',
      location: 'Okutama',
      url: '/itineraries/satologue-ozawashuzo',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/31d7de7ade3844ddaf03382292cec5fd/007.jpg',
    },
    {
      name: 'Ashinoko & Numazu — Fuji Outlook Experience',
      location: 'Shizuoka',
      url: '/itineraries/fujiseiran-ashinoko-numazu',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9ca7a5a6701646eaacb9906fa2dbb626/00278.jpg',
    },
    {
      name: 'Tokaido Heritage Walk — Fujiya Hotel Stay',
      location: 'Hakone',
      url: '/itineraries/fujiya-explorehakone',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3a00c9f21d234528aaed9801c98df19d/006.jpg',
    },
    {
      name: 'Yugawara Onsen — Mountain & Sea Serenity',
      location: 'Kanagawa',
      url: '/experiences/en_miwayugawara/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/6d8ebcddd31f42479f32a7ee5bd3bf9f/202106_R105_ohta.jpg',
    },
  ],
  'mt-fuji-hakone': [
    {
      name: 'Tour Hakone & Numazu — Luxury Stay at Fuji Seiran',
      location: 'Hakone · Numazu',
      url: '/itineraries/fujiseiran-ashinoko-numazu',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
    {
      name: 'Hakone Gora Byakudan — Premium Sake Tasting',
      location: 'Hakone',
      url: '/itineraries/hakone-byakudan-setoshuzo',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
    {
      name: 'Stay at Fujiya Hotel — Hike the Old Tokaido Road',
      location: 'Hakone',
      url: '/itineraries/fujiya-explorehakone',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
    {
      name: 'Tour Mt. Fuji & Yamanashi Wineries at nôtori',
      location: 'Yamanashi',
      url: '/itineraries/notori-thousandth-wineries',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
  ],
  'gifu-nagano': [
    {
      name: 'Rediscover Rural Japan — Family-Owned Ryokan in Hida',
      location: 'Hida Takayama',
      url: '/itineraries/busuitei',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
    {
      name: 'Unwind at a Stylish Ryokan — Artisanal Woodworking',
      location: 'Hida Takayama',
      url: '/itineraries/hidatei-kazehikaru',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1256de6b1d124ea1944a033e345a69cc/images_267478b2-ba8a-4d25-b193-fbfd8d7afb2a.jpeg',
    },
  ],
  kanazawa: [],
  kansai: [
    {
      name: 'ART MON ZEN KYOTO — Meitoku-an Tea Ceremony',
      location: 'Kyoto',
      url: '/experiences/en_meitokuan/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/24dbfa7e263148469098adde5f0cb3d7/en_meitokuan_AR05.jpg',
    },
    {
      name: 'Hiiragiya Ryokan — Forge Your Own Japanese Knife',
      location: 'Kyoto',
      url: '/itineraries/hiiragiya-kanetaka-hamono',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/24dbfa7e263148469098adde5f0cb3d7/en_meitokuan_AR05.jpg',
    },
    {
      name: 'Hiiragiya Ryokan — Traditional Silk Dyeing',
      location: 'Kyoto',
      url: '/itineraries/hiiragiya-atelier-shimura',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3fb69a9fc90c4226928f94ca83306297/en_oyashikistay_AR04.jpg',
    },
    {
      name: 'Sumiya Ryokan — Learn Kintsugi with a Local Artisan',
      location: 'Kyoto',
      url: '/itineraries/sumiya-shikata',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/24dbfa7e263148469098adde5f0cb3d7/en_meitokuan_AR05.jpg',
    },
    {
      name: 'Hiiragiya Ryokan — An Evening with a Geisha',
      location: 'Kyoto',
      url: '/itineraries/hiiragiya-geisha',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3fb69a9fc90c4226928f94ca83306297/en_oyashikistay_AR04.jpg',
    },
    {
      name: 'Traditional Thatched Villa in Miyama, Rural Kyoto',
      location: 'Kyoto Rural',
      url: '/itineraries/miyama-honkan',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/24dbfa7e263148469098adde5f0cb3d7/en_meitokuan_AR05.jpg',
    },
    {
      name: 'Escape into National Park Forests near Osaka',
      location: 'Osaka',
      url: '/experiences/en_meitokuan/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3fb69a9fc90c4226928f94ca83306297/en_oyashikistay_AR04.jpg',
    },
    {
      name: 'Kominka Folk Inn — History & Bounties in Gokasho',
      location: 'Nara',
      url: '/itineraries/nipponia-gokasho-ukarokkon',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/24dbfa7e263148469098adde5f0cb3d7/en_meitokuan_AR05.jpg',
    },
  ],
  chugoku_shikoku: [
    {
      name: 'Simose Art Garden Villa — World-Class Architecture',
      location: 'Hiroshima',
      url: '/experiences/en_simose/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3a83cdbd583247e694cba2ca17da7ebb/ryokankurashiki_AR22.jpg',
    },
    {
      name: 'Onomichi Ryokan — Sake, Soy Sauce & Zen Temple',
      location: 'Onomichi',
      url: '/itineraries/onomichi-3days',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3a83cdbd583247e694cba2ca17da7ebb/ryokankurashiki_AR22.jpg',
    },
    {
      name: 'Ryokan Kurashiki — Historic Sugar Merchant Manor',
      location: 'Kurashiki',
      url: '/experiences/en_ryokankurashiki-basic/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3a83cdbd583247e694cba2ca17da7ebb/ryokankurashiki_AR22.jpg',
    },
    {
      name: 'Private Villa on Shodoshima — Olive Groves & Pilgrimages',
      location: 'Naoshima & Islands',
      url: '/experiences/en_oyashikistay/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3a83cdbd583247e694cba2ca17da7ebb/ryokankurashiki_AR22.jpg',
    },
    {
      name: 'Seto Inland Sea — Historic Soy Sauce Brewery & Inn',
      location: 'Seto Inland Sea',
      url: '/itineraries/shintoyo-okamotoshoyu',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/3a83cdbd583247e694cba2ca17da7ebb/ryokankurashiki_AR22.jpg',
    },
  ],
  kyushu: [
    {
      name: 'Enter a World of Historic Townhouses in Sakai City',
      location: 'Osaka / Sakai',
      url: '/experiences/en_sakainoma_yanagi/',
      image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/1bd620677736489389d6781858ea9687/en_oyashikistay_PCKV.jpg',
    },
  ],
};

const REGION_DESTINATION_WEBPATHS: Record<keyof typeof AREA_INFO, string> = {
  hokkaido: '',
  tohoku: '',
  tokyo: 'kamakura,tokyo-others,tokyo',
  'mt-fuji-hakone': 'mt-fuji-around,hakone,mt-fuji-hakone-others',
  'gifu-nagano': 'hida-takayama,matsumoto,kiso,nagoya,central-japan-others',
  kanazawa: 'kanazawa,niigata,toyama,fukui,kanazawa-others',
  kansai: 'kyoto,osaka,nara,kyoto-osaka-others',
  chugoku_shikoku: 'hiroshima,onomichi,hiroshima-others,naoshima-islands,kurashiki,kagawa,okayama-naoshima-others',
  kyushu: '',
};

// All non-empty webpaths combined for the "show all" default view
const ALL_REGION_DESTINATIONS = (Object.values(REGION_DESTINATION_WEBPATHS) as string[]).filter(Boolean).join(',');

// Controls whether the default "all regions" view is active (true) or a specific region is selected (false)
const showAllRegions = ref(true);

// Reactive region key updated when user selects a region on the map
const selectedRegionKey = ref<keyof typeof AREA_INFO>('tokyo');

const currentDest = computed(() => REGION_DESTINATION_WEBPATHS[selectedRegionKey.value] ?? '');

// 体験セクションの取得先。末尾スラッシュなしで DB の web_path と一致させる。
const THEME_WEB_PAGE_PATH = '/lp/experiential-stay';

// タブ背景画像は microCMS の URL を直書きしており管理画面から入稿できない。ここに定義のあるセクションだけを
// このキー順で描画することで、運用側でセクションを追加しても画像欠けのタブが出ない。
// gaLabel は表示見出しと切り離した固定値。見出しはセクションのタイトルから描画され管理画面で変更できるため、
// タイトルを計測名にするとリネームのたびに theme_name が分岐して GA の時系列が切れる。
const THEME_TAB_ASSETS: Record<string, { image: string; gaLabel: string }> = {
  'stay-lp-scenic': {
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/93d8c0bf30894f5faa171232c2480f58/Scenic%20Journeys.jpg',
    gaLabel: 'Scenic Journeys',
  },
  'stay-lp-heritage-culture': {
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/e0870dcd03e343cfb3c40edc145eb93a/taniya-houkokusha_AR3.jpg',
    gaLabel: 'Heritage & Culture Journey',
  },
  'stay-lp-nature-wellness': {
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/e3d610bcb5b148df948a74a304aad7ea/Mask%20group.png',
    gaLabel: 'Nature & Wellness Journey',
  },
  'stay-lp-art-architecture': {
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/bf3d4beb384f431b85138452100e6c25/en_fukudamuseum_780-520_13.jpg',
    gaLabel: 'Art & Architecture Journey',
  },
  'stay-lp-local-culinary': {
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/12d61bd52bb843719ab0d2b909c417d3/Frame%201321319997.jpg',
    gaLabel: 'Local Culinary Journey',
  },
  'stay-lp-family-friendly': {
    image: 'https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/83bc7b1e4b5e45dc869b9a54c6d43ee7/Mask%20group.jpg',
    gaLabel: 'Family Friendly Journey',
  },
};

const THEME_WEB_SECTION_IDS = Object.keys(THEME_TAB_ASSETS);

const selectedThemeId = ref('');

// Fetch exactly 4 curated stories by ID from MicroCMS
const CURATED_STORY_IDS = ['ritsuko-nakamura', 'fumie-makino', 'fushi', 'hiiragiya'];

// iconic-stays（The Spirit of Place）のキュレーション。実在検証（CMS ∩ API）で存在するものだけを表示するため、
// ここに載せても実データに存在しない slug は自動的に非表示になる。
const ICONIC_STAY_SLUGS = ['en_ninnaji-stay', 'en_makinooto-geisha'];

// ギャラリーのリンクから実在検証用の slug を抽出する。末尾スラッシュの有無を吸収する。
const extractSlug = (link: string, prefix: string): string => link.replace(prefix, '').replace(/\/$/, '');
const GALLERY_EXPERIENCE_SLUGS = Array.from(new Set([...galleryItems, ...galleryModalExtraItems].filter((i) => i.link?.startsWith('/experiences/')).map((i) => extractSlug(i.link, '/experiences/'))));
const GALLERY_ITINERARY_SLUGS = Array.from(new Set([...galleryItems, ...galleryModalExtraItems].filter((i) => i.link?.startsWith('/itineraries/')).map((i) => extractSlug(i.link, '/itineraries/'))));

// iconic-stays とギャラリーの体験リンクを同一バッチで実在検証するため slug を統合する。
const EXPERIENCE_LINK_SLUGS = Array.from(new Set([...ICONIC_STAY_SLUGS, ...GALLERY_EXPERIENCE_SLUGS]));

// 互いに依存しない初期フェッチ（体験検索×2・体験セクション×1・MicroCMSストーリー×1・実在検証×3）を並列実行し、SSR時の逐次ウォーターフォールを避ける。
// 体験セクションは 6 テーマを 1 リクエストで受け取るため、タブ切替での再フェッチは要らない。
// iconic-stays / ギャラリー体験リンクの存在判定・タイトル/概要/画像は CMS（en-experiences）バッチで賄う。CMS は PRIVATE（限定公開）体験も含むため、
// 検索 API（PUBLISHED のみ）ではなく CMS を存在の基準にする。iconic のメタ（価格/泊数/エリア）は PRIVATE も返る単体 API で別途取得する。
const [experienceDataResult, allExperiencesDataResult, storyApiDataResult, themeSectionsResult, iconicCmsResult, itineraryCmsResult] = await Promise.all([
  useAsyncWabunkaExperiencesSearch({
    experienceEventStartAtDates: ref(''),
    destinations: currentDest,
    themes: ref(CURATED_STAYS_THEME_SLUG),
    slugs: ref([]),
  }).catch((e) => {
    console.error('[curated-stay] useAsyncWabunkaExperiencesSearch failed:', e);
    return null;
  }),
  useAsyncWabunkaExperiencesSearch({
    experienceEventStartAtDates: ref(''),
    destinations: ref(ALL_REGION_DESTINATIONS),
    themes: ref(CURATED_STAYS_THEME_SLUG),
    slugs: ref([]),
  }).catch((e) => {
    console.error('[curated-stay] allExperiencesData fetch failed:', e);
    return null;
  }),
  (async () => {
    try {
      const result = await useMicroCMSGetList(
        {
          endpoint: 'en-stories',
          queries: { ids: CURATED_STORY_IDS, fields: ['id', 'title', 'thumbnailImage', 'provider'], limit: 4 },
        },
        { key: 'lp-curated-stay-stories' },
      );
      return result.data;
    } catch (e) {
      console.error('[curated-stay] story fetch failed:', e);
      return null;
    }
  })(),
  // テーマタブの掲載内容・掲載順・見出しの取得元。useCurrency / inject を使うため配列内で同期的に呼ぶ必要があり、
  // async 関数で包んで await 後に呼ぶと SSR で injection context を失う。
  useGetExperienceSectionsQuery(THEME_WEB_PAGE_PATH, THEME_WEB_SECTION_IDS).catch((e) => {
    console.error('[curated-stay] themeSections fetch failed:', e);
    return null;
  }),
  // iconic-stays + ギャラリー体験リンクの存在判定・タイトル/概要/画像（en-experiences。PRIVATE 体験も含む）
  (async () => {
    try {
      const result = await useMicroCMSGetList(
        {
          endpoint: 'en-experiences',
          queries: { ids: EXPERIENCE_LINK_SLUGS, fields: ['id', 'title', 'overviewV2', 'pcMainImage', 'subImages'], limit: EXPERIENCE_LINK_SLUGS.length },
        },
        { key: 'lp-curated-stay-experiences' },
      );
      return result.data;
    } catch (e) {
      console.error('[curated-stay] iconic/gallery en-experiences fetch failed:', e);
      return null;
    }
  })(),
  // ギャラリー itinerary リンクの実在検証（en-featured: type=itinerary）
  (async () => {
    try {
      const result = await useMicroCMSGetList(
        {
          endpoint: 'en-featured',
          queries: { ids: GALLERY_ITINERARY_SLUGS, filters: 'type[equals]itinerary', fields: ['id'], limit: GALLERY_ITINERARY_SLUGS.length },
        },
        { key: 'lp-curated-stay-itineraries' },
      );
      return result.data;
    } catch (e) {
      console.error('[curated-stay] gallery itinerary fetch failed:', e);
      return null;
    }
  })(),
]);

// Fetch for the selected region (reactive — re-fetches when currentDest changes)
const experienceData: Awaited<ReturnType<typeof useAsyncWabunkaExperiencesSearch>> | null = experienceDataResult;
// Fetch for the default "all regions" view (non-reactive, fires once)
const allExperiencesData: Awaited<ReturnType<typeof useAsyncWabunkaExperiencesSearch>> | null = allExperiencesDataResult;
const storyApiData: Ref<any> | null = storyApiDataResult;
const themeSections: Awaited<ReturnType<typeof useGetExperienceSectionsQuery>> | null = themeSectionsResult;
// iconic-stays / ギャラリーの CMS（en-experiences）バッチ・itinerary（en-featured）バッチ結果
const iconicCmsData: Ref<any> | null = iconicCmsResult;
const itineraryCmsData: Ref<any> | null = itineraryCmsResult;

// iconic-stays のメタ（価格/泊数/エリア）は PRIVATE（限定公開）体験も返る単体 API で slug ごとに取得する。
// 件数が少ない（ICONIC_STAY_SLUGS）ため単体取得でもリクエスト増は限定的。存在しない slug は 503 を投げるため .catch で握る。
const iconicApiResults = await Promise.all(
  ICONIC_STAY_SLUGS.map((slug) =>
    useAsyncWabunkaExperience(ref(slug)).catch((e) => {
      console.error(`[curated-stay] iconic experience fetch failed: ${slug}`, e);
      return null;
    }),
  ),
);
const iconicApiBySlug = computed(() => {
  const map = new Map<string, any>();
  ICONIC_STAY_SLUGS.forEach((slug, idx) => {
    const r = iconicApiResults[idx];
    if (r != null && !r.isExperienceMissing.value) map.set(slug, r.experience.value);
  });
  return map;
});

const curatedStories = computed(() => {
  const contents: any[] = storyApiData?.value?.contents ?? [];
  return CURATED_STORY_IDS.map((id) => contents.find((s: any) => s.id === id)).filter((s) => s != null);
});

// overviewV2 はリッチテキスト。カードは先頭段落の抜粋だけを表示するため、
// 先頭 <p> のテキストのみ抽出する。アンカー内に <a> や見出し・リストを入れると
// <a> ネストで hydration mismatch を起こし、以降の @click が死ぬため、タグは全て除去する。
const toOverviewExcerpt = (html: string): string => {
  const body = html.replace(/^\s*<h[1-6][^>]*>\s*overview\s*<\/h[1-6]>\s*/i, '');
  const firstPara = body.match(/<p[^>]*>([\s\S]*?)<\/p>/i)?.[1] ?? body;
  return firstPara
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&apos;|&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

// iconic-stays（The Spirit of Place）: CMS に実在する slug のみを ICONIC_STAY_SLUGS の順で描画する（PRIVATE 含む）。
// タイトル/概要は CMS、エリア/泊数/価格と画像は単体 API から組み立てる。
// 画像は体験カードのサムネイルと同じ images[0]（KV ではなく体験一覧で使われる画像）を使い、
// API に画像が無いときだけ CMS の pcMainImage にフォールバックする。
const iconicStayCards = computed(() => {
  const cmsList: any[] = iconicCmsData?.value?.contents ?? [];
  const cmsById = new Map(cmsList.map((c: any) => [c.id, c]));
  const apiBySlug = iconicApiBySlug.value;
  const cards = ICONIC_STAY_SLUGS.map((slug) => {
    const cms = cmsById.get(slug);
    const api = apiBySlug.get(slug);
    if (!cms || !api) return null;
    return {
      slug,
      title: cms.title,
      overview: toOverviewExcerpt(cms.overviewV2 || ''),
      imageUrl: api.images?.[0]?.imageUrl ?? cms.pcMainImage?.url ?? '',
      location: displayDestinationTagName(api.tags),
      duration: buildExperientialStayDuration(api.experienceDuration),
      price: displayPrice(api) ?? '',
      detailLink: `/experiences/${slug}/`,
    };
  }).filter((c): c is NonNullable<typeof c> => c != null);
  // CMS 取得が成功しているときのみ欠損を warn（取得失敗を欠損と誤認しない）。
  if (cmsList.length > 0) {
    const missing = ICONIC_STAY_SLUGS.filter((s) => !cmsById.has(s) || !apiBySlug.has(s));
    if (missing.length > 0) console.warn('[curated-stay] iconic-stays: missing slugs', missing);
  }
  return cards;
});

// リンク実在検証: CMS（en-experiences。PRIVATE 含む）に存在するかで判定する。CMS 取得失敗時は判定不能として除外しない。
const verifiedExperienceSlugs = computed<Set<string> | null>(() => {
  if (iconicCmsData?.value == null) return null;
  return new Set((iconicCmsData.value.contents ?? []).map((c: any) => c.id));
});
const verifiedItinerarySlugs = computed<Set<string> | null>(() => {
  if (itineraryCmsData?.value == null) return null;
  return new Set((itineraryCmsData.value.contents ?? []).map((c: any) => c.id));
});
const isLinkVerified = (link: string | undefined): boolean => {
  if (!link) return false;
  if (link.startsWith('/experiences/')) {
    const verified = verifiedExperienceSlugs.value;
    return verified == null ? true : verified.has(extractSlug(link, '/experiences/'));
  }
  if (link.startsWith('/itineraries/')) {
    const verified = verifiedItinerarySlugs.value;
    return verified == null ? true : verified.has(extractSlug(link, '/itineraries/'));
  }
  // 検証対象外のリンク（/s/experiences 等）はそのまま表示する
  return true;
};
const verifiedGalleryItems = computed(() => galleryItems.filter((i) => isLinkVerified(i.link)));
const verifiedGalleryModalItems = computed(() => galleryModalItems.filter((i) => isLinkVerified(i.link)));

// 静的フォールバックとして表示してよいのは itinerary カードのみ（体験は API 非対象で実在検証できないため出さない）。
const toItineraryOnly = (stays: any[]): any[] => stays.filter((s) => typeof s.url === 'string' && s.url.startsWith('/itineraries/'));

// During pending re-fetch show static itinerary data immediately; once loaded, prefer live API data
const regionLoading = computed(() => experienceData?.isLoading?.value ?? false);
const regionExperiences = computed<any[]>(() => {
  const staticStays = toItineraryOnly(MAP_STAYS[selectedRegionKey.value] ?? []);
  // No webpath = no API fetch intended → itinerary の静的カードのみ表示
  if (!REGION_DESTINATION_WEBPATHS[selectedRegionKey.value]) return staticStays;
  if (regionLoading.value) return staticStays;
  const live = experienceData?.experiences?.value ?? [];
  return live.length > 0 ? live : staticStays;
});

// Combined view: live experiences from all API regions + static itinerary cards
const allRegionLoading = computed(() => allExperiencesData?.isLoading?.value ?? false);
const displayedExperiences = computed<any[]>(() => {
  if (showAllRegions.value) {
    if (allRegionLoading.value) {
      // While loading, show only static itinerary cards as placeholder
      return toItineraryOnly((Object.values(MAP_STAYS) as any[][]).flat().filter(Boolean));
    }
    const live = allExperiencesData?.experiences?.value ?? [];
    // Append Tohoku static itinerary card (not in API) to live results
    const tohokuStatic = toItineraryOnly(MAP_STAYS.tohoku);
    return live.length > 0 ? [...live, ...tohokuStatic] : toItineraryOnly((Object.values(MAP_STAYS) as any[][]).flat().filter(Boolean));
  }
  return regionExperiences.value;
});

// マップのエリア情報パネル表示を反応的な単一状態で管理する（'' はデフォルト表示）。
const activeAreaKey = ref<keyof typeof AREA_INFO | ''>('');
const activeAreaInfo = computed<any>(() => (activeAreaKey.value ? AREA_INFO[activeAreaKey.value] : null));

// スティ一覧ラベルは displayedExperiences から算出する単一の source of truth。
const mapStaysLabel = computed(() => {
  const count = displayedExperiences.value.length;
  if (showAllRegions.value) {
    return count > 0 ? `All Experiential Stays — ${count} experience${count !== 1 ? 's' : ''}` : 'All Experiential Stays';
  }
  const info = AREA_INFO[selectedRegionKey.value];
  return count > 0 ? `${info?.title} — ${count} curated experience${count !== 1 ? 's' : ''}` : `${info?.title ?? ''} —`;
});

// 直近の取得成功分を保持する。通貨切替の再取得が失敗すると useAsyncData が data を既定値へ戻すため、
// これを直に描画元にするとタブ 6 枚ごと消える。setup 時の .catch は初回フェッチにしか掛からず、
// 再取得の失敗はどこにも出ないため、記録もここで行う。
const loadedThemeSections = ref<ExperienceSectionListViewModel>([]);
watch(
  () => [themeSections?.hasSucceeded?.value, themeSections?.hasError?.value] as const,
  ([hasSucceeded, hasError]) => {
    if (hasError) {
      console.error('[curated-stay] themeSections refresh failed');
      return;
    }
    if (!hasSucceeded) return;
    loadedThemeSections.value = themeSections?.experienceSections?.value ?? [];
    // 要求した webSectionId が返ってこないのは、セクションの削除・改名・ページ紐付けの外れ。
    // 掲載管理が管理画面に移るぶん、タブが 1 枚減るだけの無言の欠落になるため記録する。
    // 全滅時は画面に "More themes coming soon." が出るので、部分欠落のみを対象にする。
    const missing = THEME_WEB_SECTION_IDS.filter((id) => !loadedThemeSections.value.some((s) => s.webSectionId === id));
    if (loadedThemeSections.value.length > 0 && missing.length > 0) {
      console.warn(`[curated-stay] missing sections (${missing.length}):`, missing);
    }
  },
  { immediate: true },
);

// 描画するタブ。画像マップに定義があり、かつセクションが取得できたものだけを、マップの定義順で並べる。
// セクション側の並び順には従わない。タブの並びは LP のレイアウト都合であり、運用の並べ替えで動くべきものではない。
const themeTabs = computed(() =>
  THEME_WEB_SECTION_IDS.flatMap((webSectionId) => {
    const section = loadedThemeSections.value.find((s) => s.webSectionId === webSectionId);
    if (!section) return [];
    return [{ webSectionId, title: section.title, experiences: section.experiences, ...THEME_TAB_ASSETS[webSectionId] }];
  }),
);

// 選択中のセクションが管理画面の削除・非公開で消えたら選択も解除する。id だけ残すと、
// どのタブもハイライトされないまま一覧だけが消えた状態になる。
watch(themeTabs, (tabs) => {
  if (selectedThemeId.value && tabs.length > 0 && !tabs.some((tab) => tab.webSectionId === selectedThemeId.value)) {
    selectedThemeId.value = '';
  }
});

const selectedThemeTab = computed(() => themeTabs.value.find((tab) => tab.webSectionId === selectedThemeId.value));
const selectedThemeTitle = computed(() => selectedThemeTab.value?.title ?? '');
// 掲載順はセクションの登録順をそのまま使う。LP 側では並べ替えない。
const themeExperiences = computed(() => selectedThemeTab.value?.experiences ?? []);

const STAYS_PAGE_SIZE_PC = 30;
const STAYS_PAGE_SIZE_MOB = 8;
const staysLimit = ref(STAYS_PAGE_SIZE_PC);
const pagedExperiences = computed(() => displayedExperiences.value.slice(0, staysLimit.value));
const hasMoreStays = computed(() => displayedExperiences.value.length > staysLimit.value);
function loadMoreStays() {
  staysLimit.value += window.innerWidth <= 768 ? STAYS_PAGE_SIZE_MOB : STAYS_PAGE_SIZE_PC;
}

function openExternal(url: string) {
  // 内部遷移は SPA 遷移でプリフェッチ・状態保持を効かせる（フルリロード回避）。
  return navigateTo(url);
}

// autoCleanup: false は、呼び出し側が戻り値の disconnect で破棄まで持つ場合に使う。
// 繰り返し呼ぶ箇所で cleanupFns に積むと、破棄済みのクロージャがアンマウントまで増え続ける。
function registerRevealAll(selector: string, staggerSec = 0.09, maxDelay = 0.72, { autoCleanup = true }: { autoCleanup?: boolean } = {}) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
  );

  const elements = document.querySelectorAll<HTMLElement>(selector);
  elements.forEach((el, index) => {
    el.classList.remove('reveal', 'is-visible');
    revealObserver.unobserve(el);
    el.classList.add('reveal');
    el.style.setProperty('--reveal-delay', `${Math.min(index * staggerSec, maxDelay)}s`);
    if (prefersReducedMotion) {
      el.classList.add('is-visible');
      return;
    }
    revealObserver.observe(el);
  });
  const disconnect = () => revealObserver.disconnect();
  if (autoCleanup) cleanupFns.push(disconnect);
  // 繰り返し呼ぶ箇所（テーマ切替）では、呼び出し側が前回分をアンマウント前に破棄できるようにする。
  return disconnect;
}

// renderArea（モバイル分岐）が参照するため initMapInteractions より前に定義する。
const MOBILE_PAGE_SIZE = 8;
const mobileSelectedRegionKey = ref<keyof typeof AREA_INFO | ''>('');
const mobileStaysLimit = ref(MOBILE_PAGE_SIZE);

// initMapInteractions 内で定義される地図ループアニメーションの停止関数。
// selectMobileDestination（モバイルのエリアカード選択）からも停止できるよう、closure の外から参照できる形で保持する。
let stopMapAnim: (() => void) | null = null;

function initMapInteractions() {
  // renderArea 内の遷移スクロール setTimeout。呼び出しごとに前回分を打ち消してから積み直す。
  let regionScrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const renderArea = (key: string) => {
    const info = AREA_INFO[key as keyof typeof AREA_INFO];
    if (!info) return;

    trackDestination(info.title);

    // パネル表示・画像・タグ・ラベルは反応的な状態に一本化し、template バインディングが追随する。
    activeAreaKey.value = key as keyof typeof AREA_INFO;
    // selectedRegionKey の更新が currentDest 再計算 → useFetch 自動再取得を誘発する
    selectedRegionKey.value = key as keyof typeof AREA_INFO;

    // マップクリック時は地域情報パネル（map-body-right / DT ではマップ横、SP ではマップ下・
    // 体験カード上に表示される "Tokyo & Around" 等のセクション）へスクロールする。
    if (window.innerWidth <= 768) {
      mobileSelectedRegionKey.value = key as keyof typeof AREA_INFO;
      mobileStaysLimit.value = MOBILE_PAGE_SIZE;
      if (regionScrollTimeoutId) clearTimeout(regionScrollTimeoutId);
      regionScrollTimeoutId = setTimeout(() => {
        const el = document.getElementById('mapBodyRight');
        if (!el) return;
        // 固定ヘッダーにセクションタイトルが隠れないよう、ヘッダー高さ分だけ上に余白を取る。
        const headerH = document.querySelector('.l-header')?.getBoundingClientRect().height ?? 0;
        const y = el.getBoundingClientRect().top + window.scrollY - headerH - 16;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      }, 150);
      return;
    }

    const section = document.getElementById('mapStaysSection');
    if (!section) return;
    section.style.display = 'block';

    // エリア切り替え時にカード一覧の先頭が見えるようにスクロール
    if (regionScrollTimeoutId) clearTimeout(regionScrollTimeoutId);
    regionScrollTimeoutId = setTimeout(() => {
      const staysEl = document.getElementById('mapStaysSection');
      if (!staysEl) return;
      const staysTop = staysEl.getBoundingClientRect().top + window.scrollY;
      // カード一覧の先頭が画面下部 240px に収まる位置へスクロール
      const targetScroll = staysTop - window.innerHeight + 240;
      if (window.scrollY < targetScroll) {
        window.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
      }
    }, 150);
  };

  // Default state: show all experiences on page load（ラベルは mapStaysLabel computed が担う）
  const staysSection = document.getElementById('mapStaysSection');
  if (staysSection) staysSection.style.display = 'block';

  // ── Map animation: cycle through enabled regions ──
  const ANIM_REGIONS = ['tokyo', 'kansai', 'mt-fuji-hakone', 'kanazawa', 'gifu-nagano', 'chugoku_shikoku'];
  let animTimer: ReturnType<typeof setInterval> | null = null;
  let animIdx = 0;
  let prevAnimEl: Element | null = null;

  const stopAnim = () => {
    if (animTimer) {
      clearInterval(animTimer);
      animTimer = null;
    }
    if (prevAnimEl) {
      prevAnimEl.classList.remove('is-map-glow');
      prevAnimEl = null;
    }
  };
  stopMapAnim = stopAnim;

  const stepAnim = () => {
    if (prevAnimEl) prevAnimEl.classList.remove('is-map-glow');
    // エリアが選択されるまで ANIM_REGIONS を無限に周回する（選択されたら stopAnim が呼ばれ再開しない）
    if (animIdx >= ANIM_REGIONS.length) animIdx = 0;
    const el = document.querySelector(`.jp-region[data-region="${ANIM_REGIONS[animIdx]}"]`) as HTMLElement | null;
    if (el) {
      el.style.animation = 'none';
      void el.offsetWidth;
      el.style.animation = '';
      el.classList.add('is-map-glow');
      prevAnimEl = el;
    }
    animIdx++;
  };
  stepAnim();
  animTimer = setInterval(stepAnim, 2200);

  // Regions with no experiences — grayed out and non-interactive
  const DISABLED_REGIONS = new Set(['hokkaido', 'kyushu']);

  const regionEls = document.querySelectorAll<HTMLElement>('.jp-region[data-region]');
  regionEls.forEach((el) => {
    const regionKey = el.dataset.region || '';
    if (DISABLED_REGIONS.has(regionKey)) {
      el.classList.add('is-disabled');
      el.removeAttribute('tabindex');
      el.removeAttribute('role');
      return;
    }
    el.style.pointerEvents = 'auto';
    const handleRegionClick = (event: MouseEvent) => {
      if (!el.contains(event.target as Node)) return;
      stopAnim();
      showAllRegions.value = false;
      regionEls.forEach((r) => r.classList.remove('is-active'));
      el.classList.add('is-active');
      renderArea(el.dataset.region || '');
    };
    const handleRegionKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      stopAnim();
      showAllRegions.value = false;
      regionEls.forEach((r) => r.classList.remove('is-active'));
      el.classList.add('is-active');
      renderArea(el.dataset.region || '');
    };
    el.addEventListener('click', handleRegionClick);
    el.addEventListener('keydown', handleRegionKeydown);
    cleanupFns.push(() => {
      el.removeEventListener('click', handleRegionClick);
      el.removeEventListener('keydown', handleRegionKeydown);
    });
  });

  const mapTabEls = document.querySelectorAll<HTMLElement>('[data-map-tab]');
  const destinationsPanel = document.getElementById('destinationsPanel');
  const themesPanel = document.getElementById('themesPanel');
  mapTabEls.forEach((tab) => {
    const handleTabClick = () => {
      mapTabEls.forEach((t) => t.classList.toggle('is-active', t === tab));
      const key = tab.getAttribute('data-map-tab');
      if (key === 'destinations' || key === 'themes') trackTab(key);
      if (destinationsPanel) destinationsPanel.style.display = key === 'destinations' ? 'block' : 'none';
      if (themesPanel) themesPanel.style.display = key === 'themes' ? 'block' : 'none';
    };
    tab.addEventListener('click', handleTabClick);
    cleanupFns.push(() => tab.removeEventListener('click', handleTabClick));
  });

  cleanupFns.push(() => {
    stopAnim();
    if (regionScrollTimeoutId) clearTimeout(regionScrollTimeoutId);
  });
}

function initGallerySlider() {
  const root = document.querySelector<HTMLElement>('[data-gallery-slider]');
  if (!root) return;
  const track = root.querySelector<HTMLDivElement>('.gallery-track');
  const viewport = root.querySelector<HTMLDivElement>('.gallery-viewport');
  const prevBtn = root.querySelector<HTMLButtonElement>('.gallery-nav--prev');
  const nextBtn = root.querySelector<HTMLButtonElement>('.gallery-nav--next');
  const slides = root.querySelectorAll<HTMLElement>('.gallery-slide');
  if (!track || !viewport || slides.length === 0) return;

  let index = 0;
  const getGap = () => parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
  const getStep = () => {
    const slide = slides[0];
    if (!slide) return 0;
    return slide.getBoundingClientRect().width + getGap();
  };
  const getVisibleCount = () => {
    const step = getStep();
    return step ? Math.max(1, (viewport.clientWidth + getGap()) / step) : 1;
  };
  const getMaxIndex = () => Math.max(0, Math.ceil(slides.length - getVisibleCount()));

  const update = () => {
    const maxIndex = getMaxIndex();
    index = Math.min(Math.max(index, 0), maxIndex);
    track.style.transform = `translateX(${-index * getStep()}px)`;
    if (prevBtn) prevBtn.disabled = index <= 0;
    if (nextBtn) nextBtn.disabled = index >= maxIndex;
  };

  const handlePrevClick = () => {
    index -= 1;
    update();
  };
  const handleNextClick = () => {
    index += 1;
    update();
  };
  prevBtn?.addEventListener('click', handlePrevClick);
  nextBtn?.addEventListener('click', handleNextClick);
  window.addEventListener('resize', update);
  window.addEventListener('load', update);
  update();

  cleanupFns.push(() => {
    prevBtn?.removeEventListener('click', handlePrevClick);
    nextBtn?.removeEventListener('click', handleNextClick);
    window.removeEventListener('resize', update);
    window.removeEventListener('load', update);
  });
}

function initGalleryModal() {
  const modal = document.getElementById('galleryModal');
  const openBtn = document.querySelector<HTMLButtonElement>('[data-gallery-open]');
  if (!modal || !openBtn) return;
  const panel = modal.querySelector<HTMLDivElement>('.gallery-modal__panel');
  const closeBtn = modal.querySelector<HTMLButtonElement>('[data-gallery-close]');
  let lastFocused: Element | null = null;

  const openModal = () => {
    lastFocused = document.activeElement as Element | null;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.body.classList.add('gallery-modal-open');
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    closeBtn?.focus();
  };

  const closeModal = () => {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    modal.hidden = true;
    document.body.classList.remove('gallery-modal-open');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    if (lastFocused && typeof (lastFocused as HTMLElement).focus === 'function') {
      (lastFocused as HTMLElement).focus();
    }
  };

  const handleModalClick = (event: MouseEvent) => {
    if (event.target === modal) closeModal();
  };
  const handlePanelClick = (event: MouseEvent) => event.stopPropagation();
  const handleModalKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && modal.classList.contains('is-open')) {
      closeModal();
    }
  };

  openBtn.addEventListener('click', openModal);
  closeBtn?.addEventListener('click', closeModal);
  modal.addEventListener('click', handleModalClick);
  panel?.addEventListener('click', handlePanelClick);
  document.addEventListener('keydown', handleModalKeydown);

  cleanupFns.push(() => {
    openBtn.removeEventListener('click', openModal);
    closeBtn?.removeEventListener('click', closeModal);
    modal.removeEventListener('click', handleModalClick);
    panel?.removeEventListener('click', handlePanelClick);
    document.removeEventListener('keydown', handleModalKeydown);
    // モーダルを開いたまま SPA 遷移・アンマウントした場合でも scroll-lock を確実に解除し、遷移先へ漏らさない。
    document.body.classList.remove('gallery-modal-open');
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
  });
}

function initHeroSlideshow() {
  const slides = document.querySelectorAll('.hero__slide');
  if (slides.length < 2) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) {
    slides.forEach((slide, index) => slide.classList.toggle('is-active', index === 0));
    return;
  }

  let index = 0;
  const intervalMs = 6200;
  const fadeMs = 2200;

  // 1枚目は HTML に is-active が初期状態で付いているため transform トランジションが発火しない。
  // 一瞬初期位置へ戻してリフローさせることで他スライドと同じパンアニメーションを起動する。
  const firstImg = slides[0]?.querySelector('img') as HTMLElement | null;
  if (firstImg) {
    firstImg.style.transition = 'none';
    firstImg.style.transform = 'translate3d(-2.4%, 0, 0) scale(1.08)';
    void firstImg.offsetWidth;
    firstImg.style.transition = '';
    firstImg.style.transform = '';
  }

  let leaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
  const intervalId = setInterval(() => {
    const previous = slides[index];
    previous.classList.remove('is-active');
    previous.classList.add('is-leaving');
    index = (index + 1) % slides.length;
    slides[index].classList.remove('is-leaving');
    slides[index].classList.add('is-active');
    if (leaveTimeoutId) clearTimeout(leaveTimeoutId);
    leaveTimeoutId = setTimeout(() => {
      previous.classList.remove('is-leaving');
    }, fadeMs);
  }, intervalMs);

  cleanupFns.push(() => {
    clearInterval(intervalId);
    if (leaveTimeoutId) clearTimeout(leaveTimeoutId);
  });
}

const THEME_PAGE_SIZE = 8;
const themeStaysLimit = ref(THEME_PAGE_SIZE);
const pagedThemeExperiences = computed(() => themeExperiences.value.slice(0, themeStaysLimit.value));
const hasMoreThemeStays = computed(() => themeExperiences.value.length > themeStaysLimit.value);
function loadMoreThemeStays() {
  themeStaysLimit.value += THEME_PAGE_SIZE;
}

// 選択状態は Vue 側で持つ。通貨切替でセクションが再取得されタブ要素が作り直されるため、
// classList への直接操作ではリスナーとハイライトが失われる。
function selectThemeTab(tab: { webSectionId: string; gaLabel: string }) {
  trackTheme(tab.gaLabel);
  selectedThemeId.value = tab.webSectionId;
  themeStaysLimit.value = THEME_PAGE_SIZE;
  nextTick(() => {
    document.getElementById('themeStaysPanel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

// ── Mobile destination card grid ──
const MOBILE_AREA_KEYS = (Object.keys(AREA_INFO) as (keyof typeof AREA_INFO)[]).filter((k) => !['hokkaido', 'kyushu'].includes(k));

const mobileMapOpen = ref(false);

const mobilePagedExperiences = computed(() => regionExperiences.value.slice(0, mobileStaysLimit.value));
const hasMobileMoreStays = computed(() => regionExperiences.value.length > mobileStaysLimit.value);

function selectMobileDestination(key: keyof typeof AREA_INFO) {
  if (mobileSelectedRegionKey.value === key) return;
  stopMapAnim?.();
  trackDestination(AREA_INFO[key].title);
  mobileSelectedRegionKey.value = key;
  selectedRegionKey.value = key;
  showAllRegions.value = false;
  mobileStaysLimit.value = MOBILE_PAGE_SIZE;
  // 体験一覧はマップの後ろに配置しているため、マップは開いたまま結果先頭へスクロールする。
  nextTick(() => {
    document.querySelector('.mob-exp-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function loadMoreMobileStays() {
  mobileStaysLimit.value += MOBILE_PAGE_SIZE;
}

onMounted(() => {
  if (window.innerWidth <= 768) staysLimit.value = STAYS_PAGE_SIZE_MOB;
  initGallerySlider();
  initGalleryModal();
  initHeroSlideshow();
  // load destination tags first, then initialize map interactions so renderArea can resolve tags
  initMapInteractions();
  registerRevealAll('.philosophy .intro-cards-head');
  registerRevealAll('.philosophy .intro-card');
  registerRevealAll('.iconic-stays-section .iconic-stays-head');
  registerRevealAll('.iconic-stays-section .iconic-stay-card');
  registerRevealAll('.map-interaction-section .map-tabs');
  registerRevealAll('.map-interaction-section .map-body');
  registerRevealAll('#themesPanel .theme-tab-grid');
  // Trigger reveal on theme cards whenever the displayed list changes
  // テーマ切替のたびに observer を作るため、前回分は onCleanup で破棄する。放置すると
  // 削除済みカードを掴んだままの observer がアンマウントまで積み上がる。
  // 破棄はここで持つので cleanupFns には積ませない。watcher 停止時にも onCleanup が走るため取りこぼさない。
  watch(themeExperiences, (_current, _previous, onCleanup) => {
    let disposed = false;
    let disconnectReveal: (() => void) | null = null;
    nextTick(() => {
      if (disposed) return;
      disconnectReveal = registerRevealAll('#themeStaysPanel .map-stay-card-wrapper', 0.06, 0.48, { autoCleanup: false });
    });
    onCleanup(() => {
      disposed = true;
      disconnectReveal?.();
    });
  });
  registerRevealAll('.gallery-section .gallery-head');
  registerRevealAll('.story-section .story-head');
  registerRevealAll('.story-section .story-card');
  registerRevealAll('.story-section .story-footer');
  registerRevealAll('.reviews-section .reviews-head');
  registerRevealAll('.reviews-section .review-card');
  registerRevealAll('.cta .cta-inner');
});
</script>

<template>
  <!-- eslint-disable vue/attributes-order -->
  <div class="lp-curated-stay">
    <main>
      <section class="hero" id="top" aria-label="KV">
        <div class="hero__slideshow" aria-hidden="true">
          <div class="hero__slide is-active">
            <OptimizedImage
              image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/c573eb3a90c142ab9a57b6518ea80346/2023_BYAKU_image_01.jpg"
              alt=""
              loading="eager"
              fetchpriority="high"
              :preload="true"
            />
          </div>
          <div class="hero__slide">
            <OptimizedImage image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/93e8f2ea9305495a977a817ca710fd32/experiential-stay_KV_2.jpg" alt="" loading="lazy" />
          </div>
          <div class="hero__slide">
            <OptimizedImage image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9ca7a5a6701646eaacb9906fa2dbb626/00278.jpg" alt="" loading="lazy" />
          </div>
        </div>

        <div class="hero-inner">
          <div class="kv-copy">
            <h1>Experiential stays</h1>
          </div>
          <div class="kv-side">
            <p>Accommodation where arriving is the beginning of the experience.</p>
            <div class="hero-actions">
              <a class="btn btn--cream" href="#map-interaction">
                Explore stays
                <span aria-hidden="true">→</span>
              </a>
              <NuxtLink class="btn btn--gold" to="/contact/" @click="trackHeroCta">
                Request an itinerary
                <span aria-hidden="true">→</span>
              </NuxtLink>
            </div>
          </div>
        </div>

        <a class="kv-scroll" href="#philosophy" aria-label="Scroll down">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M12 5V18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M7 13L12 18L17 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
      </section>

      <section class="section philosophy" id="philosophy">
        <div class="container">
          <div class="intro-cards-head">
            <h2>Some of Japan's most rewarding discoveries begin after check-in.</h2>
            <p class="lead">There is a Japan that most visitors see, and one that takes longer to find. The right stay is often what makes the difference.</p>
          </div>

          <div class="intro-card-grid">
            <article class="intro-card">
              <div class="intro-card__media">
                <OptimizedImage
                  image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/460e247f44fb413b99e513c878015e4e/en_oyashikistay_SL16.jpg"
                  alt="Selected with Local Insight"
                  loading="lazy"
                />
              </div>
              <div class="intro-card__body">
                <h3>Selected with Local Insight</h3>
                <p>
                  Every property in our collection is chosen by people who understand each area from the inside, its hidden qualities, the details that make one inn genuinely distinct from another.
                  The result is a stay with a sense of place that is difficult to find any other way.
                </p>
              </div>
            </article>

            <article class="intro-card">
              <div class="intro-card__media">
                <OptimizedImage
                  image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/52b113defcda42f19808de5bf2c02c74/D8D547D3-FEA5-4B10-9644-F4045BBF4BFF_1_105_c.jpeg"
                  alt="Beyond The Doorstep"
                  loading="lazy"
                />
              </div>
              <div class="intro-card__body">
                <h3>Beyond The Doorstep</h3>
                <p>
                  Select stays include workshops, visits, or introductions to people connected to local traditions. Meet artisans, trace ingredients back to their source, or step into corners of daily
                  life that most visitors never see.
                </p>
              </div>
            </article>

            <article class="intro-card">
              <div class="intro-card__media">
                <OptimizedImage
                  image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/bb3e7776c3b5455eb0d02e63853ce79e/images_9f25f65f-6e40-4b21-929f-efda1443eed1.jpeg"
                  alt="Traditional Character, Modern Comfort"
                  loading="lazy"
                />
              </div>
              <div class="intro-card__body">
                <h3>Traditional Character, Modern Comfort</h3>
                <p>Authentically Japanese, adapted for modern guests. Traditional design and local customs alongside the comforts that make a stay easy to live in.</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="iconic-stays-section section">
        <div class="container">
          <div class="iconic-stays-head">
            <p class="iconic-stays-label">Featured Stays</p>
            <h2 class="iconic-stays-title">The Spirit of Place</h2>
            <p class="iconic-stays-desc">
              Japan's most storied inns are extensions of the landscapes around them. Each offers a chance to experience a region through its seasons, traditions, cuisine, and the hospitality
              cultivated there over generations.
            </p>
          </div>

          <div class="iconic-stays-grid">
            <NuxtLink v-for="card in iconicStayCards" :key="card.slug" :to="card.detailLink" class="iconic-stay-card" @click="trackArticle(card.title)">
              <div class="iconic-stay-media">
                <OptimizedImage :image-url="card.imageUrl" :alt="card.title" loading="lazy" />
              </div>
              <div class="iconic-stay-body">
                <p v-if="card.location" class="iconic-stay-location">{{ card.location }}</p>
                <h3 class="iconic-stay-name">{{ card.title }}</h3>
                <div v-if="card.overview" class="iconic-stay-desc">{{ card.overview }}</div>
                <p v-if="card.duration || card.price" class="iconic-stay-meta">{{ [card.duration, card.price].filter(Boolean).join(' | ') }}</p>
                <span class="iconic-stay-cta">View Experience →</span>
              </div>
            </NuxtLink>
          </div>
        </div>
      </section>

      <section class="map-interaction-section section" id="map-interaction" aria-label="Japan map stay selector">
        <div class="container">
          <div class="map-tabs" role="tablist">
            <button type="button" class="map-tab is-active" data-map-tab="destinations">Destinations</button>
            <button type="button" class="map-tab" data-map-tab="themes">Curated Themes</button>
          </div>

          <div id="destinationsPanel">
            <!-- Mobile: destination card grid (hidden on PC via CSS) -->
            <div class="mob-dest-section">
              <div class="mob-dest-grid">
                <button v-for="key in MOBILE_AREA_KEYS" :key="key" type="button" class="mob-dest-card" :class="{ 'is-active': mobileSelectedRegionKey === key }" @click="selectMobileDestination(key)">
                  <div class="mob-dest-media">
                    <!-- OptimizedImage は動的 style（objectPosition の出し分け）を透過できないため、useOptimizedImageUrl で src のみ最適化する -->
                    <img
                      :src="getOptimizedImageUrl(AREA_INFO[key].image)"
                      :alt="AREA_INFO[key].title"
                      loading="lazy"
                      decoding="async"
                      :style="(AREA_INFO[key] as any).objectPosition ? { objectPosition: (AREA_INFO[key] as any).objectPosition } : {}"
                    />
                  </div>
                  <div class="mob-dest-overlay">
                    <p class="mob-dest-region">{{ AREA_INFO[key].region }}</p>
                    <p class="mob-dest-title">{{ AREA_INFO[key].title }}</p>
                  </div>
                </button>
              </div>

              <!-- Map accordion toggle -->
              <div class="mob-map-accordion">
                <hr class="mob-map-divider" />
                <button type="button" class="mob-map-toggle" :aria-expanded="mobileMapOpen" @click="mobileMapOpen = !mobileMapOpen">
                  <span>Explore by map</span>
                  <svg class="mob-map-chevron" :class="{ 'is-open': mobileMapOpen }" viewBox="0 0 14 14" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 5L7 10L12 5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            <div class="map-body" :class="{ 'mob-map-open': mobileMapOpen }">
              <div class="map-body-left">
                <div class="map-svg-wrap">
                  <svg id="jpMapInline" class="jp-map" viewBox="0 0 1000 1000" role="img" aria-label="Clickable Japan map" xmlns="http://www.w3.org/2000/svg" style="pointer-events: all">
                    <title>Japanese Prefectures</title>
                    <desc>Created by Geolonia (https://geolonia.com/).</desc>
                    <g transform="matrix(1.028807, 0, 0, 1.028807, -47.544239, -28.806583)">
                      <g transform="matrix(1, 0, 0, 1, 6, 18)">
                        <g class="jp-region" data-region="tohoku" tabindex="0" role="button" aria-label="Tohoku Region" style="pointer-events: all">
                          <g class="fukushima tohoku prefecture" data-code="7" transform="translate(594.000000, 511.000000)">
                            <title>福島 / Fukushima</title>
                            <polygon
                              points="82 69 72 64 72 68 66 74 56 65 55 59 49 55 37 52 12 68 4 66 5 52 0 47 4 41 2 37 3 34 12 33 13 30 21 30 19 21 28 11 25 9 32 11 38 10 40 14 44 13 47 15 53 14 56 10 54 9 55 0 61 0 65 4 74 4 75 10 80 12 79 10 83 10 83 4 87 4 93 18 93 45 91 61"
                            />
                          </g>
                          <g class="yamagata tohoku prefecture" data-code="6" transform="translate(612.000000, 439.000000)">
                            <title>山形 / Yamagata</title>
                            <polygon
                              points="14 1 24 0 28 5 42 8 44 13 48 14 53 22 51 30 48 30 52 41 45 53 46 59 43 65 36 67 37 72 36 81 38 82 35 86 29 87 26 85 22 86 20 82 14 83 7 81 4 76 7 68 7 60 15 57 17 53 8 47 8 40 0 37 13 14"
                            />
                          </g>
                          <g class="akita tohoku prefecture" data-code="5" transform="translate(617.000000, 352.000000)">
                            <title>秋田 / Akita</title>
                            <polygon
                              points="54 97 47 102 43 101 39 100 37 95 23 92 19 87 9 88 11 78 16 69 17 47 11 39 3 42 0 33 6 36 12 28 15 19 15 11 10 6 14 7 17 3 29 5 32 2 39 7 42 5 45 7 55 0 55 5 60 5 59 16 55 21 57 39 52 40 55 44 52 48 54 53 50 59 47 70 55 83 52 86 55 91"
                            />
                          </g>
                          <g class="miyagi tohoku prefecture" data-code="4" transform="translate(648.000000, 445.000000)">
                            <title>宮城 / Miyagi</title>
                            <path
                              d="M33,70 L29,70 L29,76 L25,76 L26,78 L21,76 L20,70 L11,70 L7,66 L1,66 L0,61 L7,59 L10,53 L9,47 L16,35 L12,24 L15,24 L17,16 L12,8 L16,9 L23,4 L33,9 L41,8 L39,12 L45,16 L49,11 L55,14 L57,0 L64,1 L67,8 L62,5 L63,10 L59,14 L62,19 L60,17 L56,20 L60,22 L57,28 L61,28 L61,32 L58,30 L60,34 L57,35 L58,38 L61,37 L59,39 L61,41 L61,45 L57,43 L58,40 L55,41 L57,39 L56,37 L39,40 L36,43 L40,45 L35,46 L38,47 L32,57 L33,70 Z M43,41 L44,43 L42,43 L43,41 Z"
                            />
                          </g>
                          <g class="iwate tohoku prefecture" data-code="3" transform="translate(664.000000, 354.000000)">
                            <title>岩手 / Iwate</title>
                            <polygon
                              points="48 92 41 91 39 105 33 102 29 107 23 103 25 99 17 100 7 95 8 89 5 84 8 81 0 68 3 57 7 51 5 46 8 42 5 38 10 37 8 19 12 14 16 15 29 5 31 8 35 5 40 7 46 0 54 11 52 16 57 19 55 23 61 28 64 43 62 53 65 49 66 54 68 55 62 60 64 62 67 59 67 63 63 63 63 68 60 69 65 68 60 71 62 73 60 75 64 74 58 81 62 83 57 83 60 86 57 86 59 88 53 89 52 86 54 91 52 91 52 94 50 90"
                            />
                          </g>
                          <g class="aomori tohoku prefecture" data-code="2" transform="translate(624.000000, 287.000000)">
                            <title>青森 / Aomori</title>
                            <polygon
                              points="3 71 3 63 0 60 6 51 12 51 18 47 21 31 17 27 20 26 21 19 27 23 31 20 35 23 37 38 40 45 46 41 45 37 47 33 58 42 61 39 65 23 61 16 55 22 41 25 47 0 64 11 73 6 71 37 73 51 77 62 81 61 86 67 80 74 75 72 71 75 69 72 56 82 52 81 53 70 48 70 48 65 38 72 35 70 32 72 25 67 22 70 10 68 7 72"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="hokkaido" tabindex="0" role="button" aria-label="Hokkaido" style="pointer-events: all">
                          <g class="hokkaido prefecture" data-code="1" transform="translate(602.000000, 10.000000)">
                            <title>北海道 / Hokkaido</title>
                            <path
                              d="M4,240 L3,245 L0,246 L0,237 L6,235 L4,240 Z M33,261 L32,250 L28,243 L23,242 L21,237 L17,236 L15,231 L19,223 L17,212 L19,209 L28,207 L34,198 L37,202 L39,201 L43,192 L49,187 L39,173 L40,166 L47,164 L60,174 L71,171 L71,174 L78,177 L83,174 L89,165 L83,140 L86,135 L93,132 L97,126 L96,103 L100,95 L101,85 L98,67 L90,48 L93,39 L92,33 L94,36 L99,35 L105,28 L131,55 L139,68 L155,85 L184,104 L213,109 L214,113 L219,118 L238,118 L260,91 L262,96 L252,119 L252,129 L255,135 L265,138 L263,140 L264,137 L258,137 L263,149 L269,156 L273,157 L280,149 L287,148 L277,156 L275,163 L258,166 L256,172 L252,177 L247,177 L245,175 L246,173 L243,172 L240,178 L243,180 L228,181 L220,178 L205,186 L191,202 L182,216 L179,225 L180,240 L178,248 L164,237 L141,228 L113,211 L100,209 L103,206 L88,214 L72,230 L69,227 L73,227 L68,226 L66,220 L58,212 L47,213 L42,220 L39,230 L40,234 L52,242 L62,242 L71,254 L80,257 L82,260 L76,265 L72,267 L63,263 L60,265 L60,261 L57,260 L55,265 L48,269 L48,278 L40,282 L37,287 L30,284 L27,278 L28,269 L33,261 Z M71,48 L73,45 L77,47 L79,52 L75,55 L71,52 L71,48 Z M65,35 L65,33 L67,35 L66,45 L63,35 L65,35 Z M369,17 L367,13 L365,13 L363,17 L365,23 L364,26 L359,28 L357,35 L350,34 L351,41 L341,50 L341,54 L335,54 L335,56 L339,58 L339,62 L336,65 L332,64 L332,69 L329,66 L330,71 L327,78 L331,79 L336,70 L341,69 L346,59 L356,50 L358,40 L363,43 L369,41 L384,24 L397,15 L406,13 L407,10 L404,6 L407,2 L402,0 L396,2 L384,20 L373,22 L369,17 Z M290,99 L295,93 L303,91 L308,84 L311,85 L314,79 L304,82 L296,77 L293,79 L289,89 L280,104 L280,107 L266,122 L268,129 L273,128 L273,132 L274,119 L282,114 L283,109 L286,109 L287,102 L290,99 Z M322,125 L334,115 L329,113 L325,116 L326,117 L319,119 L319,123 L321,122 L322,125 Z M300,142 L304,137 L297,139 L300,142 Z M291,146 L293,143 L289,144 L291,146 Z"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="tokyo" tabindex="0" role="button" aria-label="Tokyo &amp; Around" style="pointer-events: all">
                          <g class="kanagawa kanto prefecture" data-code="14" transform="translate(584.000000, 656.000000)">
                            <title>神奈川 / Kanagawa</title>
                            <polygon
                              points="10 0 24 6 28 11 28 6 30 6 27 4 30 2 44 9 40 13 36 12 39 16 36 16 36 23 42 26 38 30 39 33 35 33 36 28 33 24 26 22 15 25 11 28 12 34 10 34 4 30 4 17 0 17 9 9"
                            />
                          </g>
                          <g class="tokyo kanto prefecture" data-code="13" transform="translate(585.000000, 642.000000)">
                            <title>東京 / Tokyo</title>
                            <path
                              d="M49,173 L49,178 L44,171 L49,173 Z M34,113 L32,115 L29,114 L31,111 L34,113 Z M11,104 L13,106 L11,107 L11,104 Z M18,98 L16,96 L18,92 L18,98 Z M22,75 L22,69 L26,70 L26,76 L22,75 Z M48,7 L49,12 L47,16 L41,15 L43,19 L39,20 L43,20 L43,23 L29,16 L26,18 L29,20 L27,20 L27,25 L23,20 L9,14 L4,11 L0,3 L3,0 L18,4 L23,8 L30,5 L30,9 L36,6 L40,7 L42,5 L48,7 Z"
                            />
                          </g>
                          <g class="chiba kanto prefecture" data-code="12" transform="translate(627.000000, 629.000000)">
                            <title>千葉 / Chiba</title>
                            <polygon
                              points="5 29 7 25 6 20 6 13 0 0 8 10 19 15 37 11 37 8 55 20 55 24 45 24 34 35 32 57 19 61 7 75 0 71 5 69 3 66 4 63 3 58 5 53 1 49 4 45 7 46 7 42 13 39 18 33 11 26 6 30"
                            />
                          </g>
                          <g class="saitama kanto prefecture" data-code="11" transform="translate(573.000000, 618.000000)">
                            <title>埼玉 / Saitama</title>
                            <polygon points="48 4 49 5 51 12 54 11 54 11 60 24 60 31 54 29 52 31 48 30 42 33 42 29 35 32 30 28 15 24 12 27 1 24 0 19 16 10 21 0 32 2 38 6" />
                          </g>
                          <g class="gunma kanto prefecture" data-code="10" transform="translate(557.000000, 570.000000)">
                            <title>群馬 / Gunma</title>
                            <polygon
                              points="64 52 54 54 48 50 37 48 32 58 16 67 12 65 12 59 9 56 12 55 10 49 13 47 12 41 3 41 0 36 1 29 6 25 5 23 14 20 26 14 26 11 29 10 28 5 32 4 34 0 41 7 49 9 47 12 50 14 47 17 46 26 54 29 48 42 53 49 62 48"
                            />
                          </g>
                          <g class="tochigi kanto prefecture" data-code="9" transform="translate(603.000000, 563.000000)">
                            <title>栃木 / Tochigi</title>
                            <polygon points="19 60 18 59 16 55 7 56 2 49 8 36 0 33 1 24 4 21 1 19 3 16 28 0 40 3 46 7 47 13 47 24 48 27 45 29 47 39 44 46 33 48 30 53 27 53 26 57" />
                          </g>
                          <g class="ibaraki kanto prefecture" data-code="8" transform="translate(622.000000, 575.000000)">
                            <title>茨城 / Ibaraki</title>
                            <polygon
                              points="5 54 5 54 2 55 0 48 7 45 8 41 11 41 14 36 25 34 28 27 26 17 29 15 28 12 28 1 38 10 44 4 44 0 54 5 46 29 47 35 44 42 46 52 51 61 49 63 51 66 52 62 60 74 42 62 42 65 24 69 13 64"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="mt-fuji-hakone" tabindex="0" role="button" aria-label="Mt. Fuji, Hakone &amp; Around" style="pointer-events: all">
                          <g class="shizuoka chubu prefecture" data-code="22" transform="translate(511.000000, 659.000000)">
                            <title>静岡 / Shizuoka</title>
                            <polygon
                              points="73 14 77 14 77 27 83 31 81 36 85 42 85 46 78 55 77 61 70 65 64 59 67 45 65 41 66 38 72 37 62 31 55 33 51 38 53 41 44 45 42 54 36 61 38 65 25 61 0 60 0 52 8 47 18 27 33 17 33 5 37 0 40 8 39 19 44 20 48 29 53 27 52 20 56 12 61 17"
                            />
                          </g>
                          <g class="yamanashi chubu prefecture" data-code="19" transform="translate(546.000000, 638.000000)">
                            <title>山梨 / Yamanashi</title>
                            <polygon points="2 21 0 16 3 14 0 11 10 0 15 5 21 3 25 6 28 4 39 7 43 15 48 18 47 27 38 35 26 38 21 33 17 41 18 48 13 50 9 41 4 40 5 29" />
                          </g>
                        </g>
                        <g class="jp-region" data-region="gifu-nagano" tabindex="0" role="button" aria-label="Gifu, Nagano &amp; Around" style="pointer-events: all">
                          <g class="aichi chubu prefecture" data-code="23" transform="translate(469.000000, 673.000000)">
                            <title>愛知 / Aichi</title>
                            <polygon
                              points="42 46 18 52 20 47 23 49 31 43 33 46 35 43 34 40 28 37 26 41 18 40 14 37 16 29 12 40 16 45 13 45 9 41 10 36 8 33 8 28 12 21 5 25 0 17 1 11 5 3 15 0 21 8 26 10 33 8 39 12 46 8 47 14 52 11 60 13 50 33 42 38"
                            />
                          </g>
                          <g class="gifu chubu prefecture" data-code="21" transform="translate(450.000000, 608.000000)">
                            <title>岐阜 / Gifu</title>
                            <polygon
                              points="66 4 69 10 64 20 67 24 66 28 60 35 55 35 53 40 59 43 64 50 62 53 68 59 66 63 68 66 65 66 67 70 65 73 58 77 52 73 45 75 40 73 34 65 24 68 20 76 19 82 12 75 6 77 4 76 8 66 7 63 4 56 1 56 0 50 5 41 11 44 12 42 25 41 28 37 22 29 24 23 29 13 26 10 30 5 34 7 35 11 44 0 47 2 52 0 52 2 56 0"
                            />
                          </g>
                          <g class="nagano chubu prefecture" data-code="20" transform="translate(503.000000, 572.000000)">
                            <title>長野 / Nagano</title>
                            <polygon
                              points="68 18 59 21 60 23 55 27 54 34 57 39 66 39 67 45 64 47 66 53 63 54 66 57 66 63 70 65 71 70 68 72 64 69 58 71 53 66 43 77 46 80 43 82 45 87 41 92 41 104 26 114 18 112 13 115 12 109 14 106 12 102 15 102 13 99 15 95 9 89 11 86 6 79 0 76 2 71 7 71 13 64 14 60 11 56 16 46 13 40 19 32 18 29 21 28 22 17 27 10 27 7 34 8 34 13 36 14 44 10 48 12 48 7 53 2 60 0 63 2 63 7 68 11"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="kanazawa" tabindex="0" role="button" aria-label="Kanazawa &amp; Around" style="pointer-events: all">
                          <g class="fukui chubu prefecture" data-code="18" transform="translate(408.000000, 618.000000)">
                            <title>福井 / Fukui</title>
                            <polygon
                              points="40 0 46 8 56 9 61 14 66 13 64 19 70 27 67 31 54 32 53 34 47 31 42 40 35 37 36 45 33 44 33 47 27 50 24 48 21 55 18 54 15 58 3 56 0 51 1 45 3 49 10 46 6 49 13 50 15 47 13 48 13 45 19 47 17 45 20 43 18 40 26 41 25 35 28 33 29 38 31 39 32 32 25 19 34 6 34 2"
                            />
                          </g>
                          <g class="ishikawa chubu prefecture" data-code="17" transform="translate(448.000000, 541.000000)">
                            <title>石川 / Ishikawa</title>
                            <path
                              d="M37,26 L34,23 L41,23 L37,26 Z M28,77 L31,80 L26,90 L21,91 L16,86 L6,85 L0,77 L15,62 L26,44 L27,33 L25,29 L25,24 L22,23 L27,10 L52,0 L56,1 L57,5 L51,6 L50,15 L46,14 L39,22 L37,18 L34,19 L32,28 L38,30 L41,26 L41,36 L33,38 L31,48 L28,50 L30,54 L28,57 L29,61 L27,69 L28,77 Z"
                            />
                          </g>
                          <g class="toyama chubu prefecture" data-code="16" transform="translate(475.000000, 575.000000)">
                            <title>富山 / Toyama</title>
                            <polygon points="41 37 31 33 27 35 27 33 22 35 19 33 10 44 9 40 5 38 1 43 0 35 2 27 1 23 3 20 1 16 4 14 6 4 14 2 11 8 23 15 31 11 33 3 43 0 47 3 50 14 49 25 46 26 47 29" />
                          </g>
                          <g class="niigata chubu prefecture" data-code="15" transform="translate(518.000000, 476.000000)">
                            <title>新潟 / Niigata</title>
                            <path
                              d="M7,113 L4,102 L0,99 L14,94 L23,87 L30,87 L45,75 L55,60 L60,46 L79,34 L80,36 L79,35 L85,28 L94,0 L102,3 L102,10 L111,16 L109,20 L101,23 L101,31 L98,39 L101,44 L104,46 L95,56 L97,65 L89,65 L88,68 L79,69 L78,72 L80,76 L76,82 L81,87 L80,101 L73,94 L71,98 L67,99 L68,104 L65,105 L65,108 L53,114 L53,107 L48,103 L48,98 L45,96 L38,98 L33,103 L33,108 L29,106 L21,110 L19,109 L19,104 L12,103 L12,106 L7,113 Z M36,46 L28,47 L35,38 L33,35 L30,37 L30,31 L34,23 L43,14 L39,30 L46,31 L43,40 L36,46 Z"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="kansai" tabindex="0" role="button" aria-label="Kyoto/Osaka &amp; Around" style="pointer-events: all">
                          <g class="wakayama kinki prefecture" data-code="30" transform="translate(385.000000, 737.000000)">
                            <title>和歌山 / Wakayama</title>
                            <path
                              d="M41,32 L44,38 L49,41 L45,46 L46,50 L38,55 L37,59 L35,59 L36,56 L19,51 L14,44 L17,41 L9,37 L4,30 L0,30 L3,25 L0,24 L6,21 L1,18 L5,16 L4,14 L8,14 L0,6 L2,3 L5,6 L31,0 L35,9 L31,10 L25,17 L30,24 L28,30 L41,32 Z M49,23 L49,26 L44,29 L44,26 L49,23 Z"
                            />
                          </g>
                          <g class="nara kinki prefecture" data-code="29" transform="translate(410.000000, 712.000000)">
                            <title>奈良 / Nara</title>
                            <polygon
                              points="24 48 19 51 19 54 17 54 17 57 16 57 3 55 5 49 0 42 6 35 10 34 6 25 8 20 6 11 11 0 17 5 21 2 25 5 27 3 29 7 26 8 28 9 27 13 36 18 28 24 31 29 29 30 31 33 29 37 30 44 29 47"
                            />
                          </g>
                          <g class="hyogo kinki prefecture" data-code="28" transform="translate(345.000000, 655.000000)">
                            <title>兵庫 / Hyogo</title>
                            <path
                              d="M31,90 L23,93 L23,88 L20,89 L20,86 L38,67 L39,69 L32,79 L35,88 L31,90 Z M3,59 L4,56 L0,50 L2,47 L1,40 L9,32 L8,26 L14,24 L14,20 L7,3 L16,0 L33,1 L32,5 L36,10 L41,9 L42,11 L42,17 L36,17 L35,23 L42,28 L47,26 L49,33 L59,35 L58,40 L55,41 L56,45 L62,48 L60,49 L62,60 L59,62 L53,60 L40,66 L25,56 L11,57 L11,53 L8,58 L5,56 L3,59 Z"
                            />
                          </g>
                          <g class="osaka kinki prefecture" data-code="27" transform="translate(387.000000, 695.000000)">
                            <title>大阪 / Osaka</title>
                            <polygon points="16 0 24 8 28 5 27 8 31 9 34 17 29 28 31 37 29 42 3 48 0 45 7 44 14 37 17 28 19 30 17 22 20 20 18 9 20 8 14 5 13 1" />
                          </g>
                          <g class="kyoto kinki prefecture" data-code="26" transform="translate(377.000000, 649.000000)">
                            <title>京都 / Kyoto</title>
                            <polygon
                              points="44 63 41 55 37 54 38 51 34 54 26 46 27 41 17 39 15 32 10 34 3 29 4 23 10 23 10 17 9 15 4 16 0 11 1 7 3 10 2 8 19 0 23 7 15 14 18 15 20 11 20 15 24 17 23 21 25 18 27 19 25 14 31 11 32 14 31 20 34 25 46 27 51 32 48 46 51 56 55 56 59 62 60 66 58 68 54 65 50 68"
                            />
                          </g>
                          <g class="shiga kinki prefecture" data-code="25" transform="translate(423.000000, 655.000000)">
                            <title>滋賀 / Shiga</title>
                            <path
                              d="M20,0 L27,3 L28,9 L31,9 L34,16 L35,19 L31,29 L33,30 L34,40 L30,50 L24,53 L17,50 L16,55 L13,56 L9,50 L5,50 L2,40 L5,26 L0,21 L3,17 L6,18 L9,11 L12,13 L18,10 L18,7 L21,8 L20,0 Z M20,13 L20,16 L18,13 L14,16 L16,24 L8,32 L8,36 L5,42 L7,45 L9,36 L15,35 L16,31 L25,25 L26,21 L20,13 Z"
                            />
                          </g>
                          <g class="mie kinki prefecture" data-code="24" transform="translate(426.000000, 683.000000)">
                            <title>三重 / Mie</title>
                            <polygon
                              points="8 95 3 92 0 86 1 86 1 83 3 83 8 80 8 77 13 76 14 73 13 66 15 62 13 59 15 58 12 53 20 47 11 42 12 38 10 37 13 36 11 32 10 28 13 27 14 22 21 25 27 22 31 12 30 2 36 0 43 7 48 15 42 16 43 20 36 32 35 40 51 47 53 52 56 51 55 55 50 55 55 56 55 61 51 62 53 60 50 58 49 60 43 61 46 57 42 57 39 62 39 60 36 63 34 61 34 64 32 62 25 66 22 68 23 73 21 72 22 70 18 73 22 77 20 77 22 80 19 78 20 81 13 85"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="chugoku_shikoku" tabindex="0" role="button" aria-label="Hiroshima &amp; Around / Okayama/Naoshima &amp; Around" style="pointer-events: all">
                          <g class="kochi shikoku prefecture" data-code="39" transform="translate(251.000000, 765.000000)">
                            <title>高知 / Kochi</title>
                            <path
                              d="M61,0 L74,6 L80,4 L81,12 L88,13 L86,17 L88,21 L94,22 L87,41 L75,25 L65,21 L55,23 L55,21 L56,24 L44,28 L50,28 L44,29 L42,32 L41,29 L38,33 L39,38 L37,44 L34,44 L30,52 L25,53 L25,61 L22,64 L25,70 L21,66 L14,68 L9,65 L5,67 L6,61 L10,60 L9,57 L6,57 L8,54 L5,41 L9,44 L15,36 L20,33 L16,24 L27,23 L30,13 L37,4 L61,0 Z M1,69 L0,70 L0,67 L1,69 Z"
                            />
                          </g>
                          <g class="ehime shikoku prefecture" data-code="38" transform="translate(225.000000, 737.000000)">
                            <title>愛媛 / Ehime</title>
                            <path
                              d="M34,21 L31,20 L35,17 L34,21 Z M55,6 L51,6 L55,0 L57,3 L55,6 Z M60,5 L61,5 L60,7 L57,6 L60,5 Z M58,10 L54,12 L55,7 L60,8 L58,10 Z M32,85 L25,83 L25,86 L23,86 L24,81 L27,82 L21,76 L24,76 L22,72 L25,72 L22,71 L23,69 L20,67 L24,70 L28,66 L28,63 L24,63 L27,60 L19,60 L21,57 L19,56 L22,51 L16,50 L8,56 L0,57 L35,37 L41,18 L50,15 L48,11 L51,10 L59,24 L70,20 L80,22 L84,18 L89,20 L87,28 L63,32 L56,41 L53,51 L42,52 L46,61 L41,64 L35,72 L31,69 L34,82 L32,85 Z"
                            />
                          </g>
                          <g class="kagawa shikoku prefecture" data-code="37" transform="translate(308.000000, 724.000000)">
                            <title>香川 / Kagawa</title>
                            <path
                              d="M6,33 L1,31 L4,21 L0,17 L6,20 L19,10 L26,13 L30,9 L31,14 L34,11 L37,14 L36,17 L45,22 L44,25 L31,23 L22,30 L15,27 L6,33 Z M27,5 L25,4 L29,4 L27,5 Z M40,0 L42,0 L40,8 L38,7 L38,5 L35,9 L35,5 L31,3 L40,0 Z"
                            />
                          </g>
                          <g class="tokushima shikoku prefecture" data-code="36" transform="translate(312.000000, 744.000000)">
                            <title>徳島 / Tokushima</title>
                            <path
                              d="M50,2 L52,0 L51,4 L50,2 Z M41,2 L49,0 L49,3 L52,4 L48,13 L55,20 L50,24 L57,26 L38,36 L33,43 L27,42 L25,38 L27,34 L20,33 L19,25 L13,27 L0,21 L2,13 L11,7 L18,10 L27,3 L40,5 L41,2 Z"
                            />
                          </g>
                          <g class="yamaguchi chugoku prefecture" data-code="35" transform="translate(168.000000, 710.000000)">
                            <title>山口 / Yamaguchi</title>
                            <path
                              d="M45,0 L47,6 L43,11 L45,15 L50,15 L48,20 L50,24 L56,22 L58,24 L61,20 L60,16 L64,14 L64,21 L66,23 L67,29 L72,31 L72,36 L69,36 L70,44 L65,46 L65,54 L61,48 L57,48 L52,42 L47,44 L50,41 L46,38 L35,42 L32,39 L30,42 L29,40 L27,42 L27,38 L25,43 L21,45 L19,43 L16,45 L15,40 L9,36 L3,43 L3,36 L0,32 L4,27 L1,21 L4,17 L10,17 L5,15 L7,12 L16,14 L17,17 L24,17 L30,15 L29,12 L41,1 L43,2 L45,0 Z M64,54 L59,56 L62,52 L64,54 Z M75,50 L83,48 L78,50 L77,53 L73,50 L69,53 L67,48 L70,46 L75,50 Z M18,15 L18,13 L22,14 L18,15 Z M51,46 L48,46 L51,43 L51,46 Z"
                            />
                          </g>
                          <g class="hiroshima chugoku prefecture" data-code="34" transform="translate(230.000000, 687.000000)">
                            <title>広島 / Hiroshima</title>
                            <path
                              d="M73,40 L72,42 L69,40 L72,43 L70,42 L68,47 L63,45 L63,42 L54,44 L51,48 L39,49 L37,53 L30,55 L28,53 L25,56 L26,53 L23,47 L25,45 L17,44 L9,51 L10,54 L5,52 L4,46 L2,44 L2,37 L0,35 L5,30 L7,24 L6,22 L12,17 L20,19 L22,16 L27,18 L35,16 L32,11 L38,8 L45,0 L58,2 L65,4 L68,7 L66,14 L70,19 L69,24 L73,40 Z M45,53 L43,56 L41,53 L46,51 L45,53 Z M21,54 L20,50 L23,51 L22,59 L19,59 L21,56 L17,51 L21,54 Z M15,48 L16,50 L12,52 L15,48 Z M35,57 L33,56 L36,56 L35,57 Z M24,58 L26,58 L25,61 L28,61 L26,63 L20,62 L23,56 L26,56 L24,58 Z M59,49 L59,52 L56,49 L59,49 Z M57,50 L53,52 L54,50 L57,50 Z M60,44 L62,45 L59,47 L60,44 Z"
                            />
                          </g>
                          <g class="okayama chugoku prefecture" data-code="33" transform="translate(295.000000, 673.000000)">
                            <title>岡山 / Okayama</title>
                            <polygon
                              points="58 8 59 14 51 22 52 29 50 32 54 38 53 41 46 39 49 42 43 49 34 48 39 49 36 53 34 52 33 57 27 55 25 58 23 52 23 55 19 51 15 55 10 52 12 56 8 54 4 38 5 33 1 28 3 21 0 18 1 15 8 14 7 10 14 10 13 7 15 6 18 0 25 2 29 7 39 1 38 3 45 6 46 12"
                            />
                          </g>
                          <g class="shimane chugoku prefecture" data-code="32" transform="translate(211.000000, 610.000000)">
                            <title>島根 / Shimane</title>
                            <path
                              d="M74,14 L78,13 L75,17 L72,15 L73,19 L71,16 L74,14 Z M79,16 L77,19 L77,15 L79,14 L81,17 L79,16 Z M83,5 L88,0 L93,5 L92,9 L89,9 L91,11 L86,12 L83,5 Z M2,100 L10,98 L25,84 L35,78 L41,70 L52,65 L55,59 L53,55 L70,51 L76,46 L79,49 L87,48 L84,50 L82,51 L88,59 L87,68 L79,70 L81,73 L77,79 L64,77 L57,85 L51,88 L54,93 L46,95 L41,93 L39,96 L31,94 L25,99 L26,101 L24,107 L19,112 L21,114 L17,116 L18,120 L15,124 L13,122 L7,124 L5,120 L7,115 L2,115 L0,111 L4,106 L2,100 Z"
                            />
                          </g>
                          <g class="tottori chugoku prefecture" data-code="31" transform="translate(288.000000, 658.000000)">
                            <title>鳥取 / Tottori</title>
                            <polygon
                              points="7 33 0 31 4 25 2 22 10 20 11 11 5 3 7 2 9 6 15 8 23 4 34 6 55 5 64 0 71 17 71 21 65 23 53 27 52 21 45 18 46 16 36 22 32 17 25 15 22 21 20 22 21 25 14 25 15 29 8 30"
                            />
                          </g>
                        </g>
                        <g class="jp-region" data-region="kyushu" tabindex="0" role="button" aria-label="Kyushu" style="pointer-events: all">
                          <g class="okinawa kyushu-okinawa prefecture" data-code="47" transform="translate(52.000000, 193.000000)">
                            <title>沖縄 / Okinawa</title>
                            <polygon points="4 109 6 110 4 111 0 110" />
                            <polygon points="48 121 55 123 51 129 39 124 42 122 44 125 46 118" />
                            <polygon points="132 113 130 110 132 110 130 108 133 105 132 100 135 108 142 114" />
                            <polygon points="225 23 224 28 219 23 223 21" />
                            <polygon points="73 117 77 112 79 113 73 120 72 126 66 127 64 125 67 122 63 119 71 120" />
                            <polygon
                              points="287 20 291 17 286 15 285 8 292 10 292 14 299 13 298 11 307 0 309 5 309 9 305 15 300 15 299 19 293 19 294 21 288 25 281 25 286 34 281 31 276 39 280 41 270 46 270 38 277 32 275 24 280 25"
                            />
                            <polygon points="127 106 126 103 128 104" />
                            <polygon points="279 8 279 6 283 8" />
                            <polygon points="293 11 294 13 292 12" />
                          </g>
                          <g class="kagoshima kyushu kyushu-okinawa prefecture" data-code="46" transform="translate(96.000000, 17.000000)">
                            <title>鹿児島 / Kagoshima</title>
                            <polygon points="23 949 26 951 23 952" />
                            <polygon points="33 960 32 954 39 950 48 956 47 961 41 965 35 964" />
                            <polygon points="64 953 64 958 59 959 58 952 62 949 63 939 70 929 71 938" />
                            <polygon
                              points="38 844 43 848 52 844 57 850 56 852 61 859 66 864 65 869 70 871 73 880 80 881 80 888 78 891 75 890 70 896 75 899 73 903 76 902 69 905 66 910 50 918 50 913 55 910 59 898 53 889 54 884 48 881 53 879 54 883 57 884 61 878 54 872 50 874 44 887 46 898 51 901 49 906 43 907 40 901 27 901 28 897 25 896 27 894 22 890 31 890 35 880 32 871 27 867 30 860 28 848 31 846 35 848"
                            />
                            <polygon points="31 837 34 836 33 838" />
                            <polygon points="27 848 24 842 29 840 30 844" />
                            <polygon points="4 868 6 865 7 867 2 875 0 874" />
                            <polygon points="12 864 9 861 14 861" />
                            <polygon points="284 149 280 150 279 146 289 144" />
                            <polygon points="301 126 301 118 306 118 305 122 309 126 308 129 303 132 300 128" />
                            <polygon points="363 98 360 99 359 97 365 93" />
                            <polygon points="344 90 331 98 335 101 330 104 327 103 329 107 323 102 325 99 316 97 324 97 325 95 321 94 335 88 337 90 339 86 344 85 342 89 344 87 345 89 347 82 349 88" />
                            <polygon points="324 108 322 106 320 108 318 101 323 102 322 105 328 107 325 109 326 107" />
                            <polygon points="355 12 352 16 352 13" />
                            <polygon points="361 1 363 0 365 4" />
                          </g>
                          <g class="miyazaki kyushu kyushu-okinawa prefecture" data-code="45" transform="translate(152.000000, 824.000000)">
                            <title>宮崎 / Miyazaki</title>
                            <polygon
                              points="36 0 38 1 43 0 48 5 56 5 59 1 63 2 63 7 65 7 54 17 53 21 56 22 52 23 51 25 53 26 47 34 41 51 39 74 34 79 32 91 27 89 22 84 24 81 24 74 17 73 14 64 9 62 10 57 5 52 0 45 1 43 22 41 19 35 23 30 18 23 18 16 23 14 32 0"
                            />
                          </g>
                          <g class="oita kyushu kyushu-okinawa prefecture" data-code="44" transform="translate(163.000000, 771.000000)">
                            <title>大分 / Oita</title>
                            <polygon
                              points="0 34 3 29 0 26 2 24 1 19 5 13 12 9 19 10 20 3 33 7 38 0 47 4 49 10 46 18 43 17 43 20 35 22 36 26 40 28 56 27 50 36 56 36 53 38 56 40 61 38 62 41 57 41 55 45 65 49 59 49 61 51 57 54 60 55 54 56 54 60 52 60 52 55 48 54 45 58 37 58 32 53 27 54 25 53 21 49 22 43 15 30 9 30 10 36 8 39"
                            />
                          </g>
                          <g class="kumamoto kyushu kyushu-okinawa prefecture" data-code="43" transform="translate(115.000000, 800.000000)">
                            <title>熊本 / kumamoto</title>
                            <path
                              d="M8,37 L13,38 L13,48 L5,57 L1,58 L1,52 L5,51 L0,50 L4,40 L3,37 L8,37 Z M20,47 L14,47 L12,43 L19,39 L26,39 L23,47 L20,47 Z M24,34 L26,34 L26,38 L23,38 L24,34 Z M38,67 L33,61 L24,65 L19,61 L32,46 L30,40 L32,41 L31,39 L37,34 L26,34 L34,28 L35,23 L27,15 L26,10 L31,10 L30,7 L35,3 L39,4 L40,0 L48,5 L56,10 L58,7 L57,1 L63,1 L70,14 L69,20 L73,24 L69,24 L60,38 L55,40 L55,47 L60,54 L56,59 L59,65 L38,67 Z"
                            />
                          </g>
                          <g class="nagasaki kyushu kyushu-okinawa prefecture" data-code="42" transform="translate(44.000000, 700.000000)">
                            <title>長崎 / Nagasaki</title>
                            <path
                              d="M53,1 L55,0 L57,2 L54,5 L55,9 L49,15 L48,19 L51,18 L49,22 L51,21 L48,25 L48,20 L46,22 L46,19 L45,22 L42,20 L44,20 L45,15 L47,15 L45,14 L49,10 L46,8 L48,3 L52,3 L53,1 Z M46,29 L43,35 L37,36 L40,22 L41,24 L45,25 L44,22 L45,26 L48,25 L46,29 Z M67,59 L68,61 L64,63 L60,59 L62,59 L61,56 L64,53 L67,55 L66,57 L68,58 L67,59 Z M28,110 L25,115 L26,110 L23,107 L26,106 L26,102 L28,102 L30,94 L28,105 L33,106 L30,108 L28,106 L28,110 Z M24,112 L21,111 L21,109 L23,111 L22,108 L24,112 Z M19,113 L20,110 L19,116 L16,111 L19,113 Z M6,118 L9,117 L10,119 L12,115 L16,125 L9,124 L9,129 L0,126 L1,124 L3,126 L4,125 L2,125 L5,122 L4,116 L6,118 Z M65,80 L62,81 L64,78 L65,80 Z M67,82 L70,84 L67,86 L67,82 Z M29,88 L31,85 L33,87 L29,88 Z M54,76 L51,77 L55,74 L54,76 Z M66,86 L64,89 L66,96 L73,98 L72,102 L78,109 L86,112 L79,117 L92,118 L94,123 L92,130 L82,134 L80,128 L85,124 L84,122 L79,121 L71,123 L68,129 L60,134 L64,129 L63,126 L65,127 L67,123 L64,125 L63,118 L59,117 L56,111 L59,100 L63,104 L62,108 L63,106 L66,108 L64,115 L75,118 L71,112 L72,107 L69,103 L66,105 L65,99 L62,99 L62,97 L58,100 L60,98 L57,96 L57,93 L57,95 L53,93 L57,87 L54,86 L55,83 L60,82 L60,85 L65,84 L66,86 Z M49,88 L48,92 L42,94 L47,91 L45,89 L48,84 L52,84 L54,81 L55,84 L49,88 Z M15,115 L15,113 L18,116 L15,118 L15,115 Z M64,101 L63,104 L62,101 L64,98 L64,101 Z"
                            />
                          </g>
                          <g class="saga kyushu kyushu-okinawa prefecture" data-code="41" transform="translate(108.000000, 773.000000)">
                            <title>佐賀 / Saga</title>
                            <polygon points="15 6 28 6 34 12 41 10 40 16 32 21 30 28 25 23 19 29 22 39 14 36 8 29 9 25 2 23 0 16 2 13 5 17 4 13 6 9 2 6 3 4 6 6 6 0 7 2 11 2 12 7" />
                          </g>
                          <g class="fukuoka kyushu kyushu-okinawa prefecture" data-code="40" transform="translate(123.000000, 752.000000)">
                            <title>福岡 / Fukuoka</title>
                            <polygon
                              points="40 53 32 48 31 52 27 51 22 55 23 58 18 58 18 52 15 49 17 42 25 37 26 31 19 33 13 27 0 27 7 23 3 20 10 15 13 21 20 20 21 15 17 17 14 14 19 15 23 12 25 5 35 3 36 0 42 1 38 5 41 5 43 2 46 4 53 0 49 8 52 10 50 11 55 22 60 22 59 29 52 28 45 32 41 38 42 43 40 45 43 48"
                            />
                          </g>
                        </g>
                      </g>
                    </g>
                    <!-- City name labels — outside transforms, coordinates in SVG root space -->
                    <g id="mapCityLabels" pointer-events="none" fill="#c6a873" font-family="Spectral, serif" font-size="28" letter-spacing="0.07em">
                      <!-- Tokyo: long line to the right -->
                      <circle cx="590" cy="668" r="5" />
                      <line x1="596" y1="668" x2="668" y2="668" stroke="#c6a873" stroke-width="1.5" />
                      <text x="672" y="676" text-anchor="start">Tokyo</text>
                      <!-- Hakone: right and lower, below Tokyo -->
                      <circle cx="552" cy="690" r="5" />
                      <line x1="557" y1="693" x2="626" y2="712" stroke="#c6a873" stroke-width="1.5" />
                      <text x="630" y="720" text-anchor="start">Hakone</text>
                      <!-- Mt. Fuji: lower-left, away from Hakone -->
                      <circle cx="531" cy="703" r="5" />
                      <line x1="526" y1="707" x2="474" y2="760" stroke="#c6a873" stroke-width="1.5" />
                      <text x="470" y="768" text-anchor="end">Mt. Fuji</text>
                      <!-- Kanazawa: upper-left, above Kyoto/Osaka -->
                      <circle cx="435" cy="603" r="5" />
                      <line x1="431" y1="600" x2="352" y2="566" stroke="#c6a873" stroke-width="1.5" />
                      <text x="348" y="574" text-anchor="end">Kanazawa</text>
                      <!-- Kyoto: long line to the left -->
                      <circle cx="369" cy="681" r="5" />
                      <line x1="364" y1="681" x2="284" y2="681" stroke="#c6a873" stroke-width="1.5" />
                      <text x="280" y="689" text-anchor="end">Kyoto</text>
                      <!-- Osaka: left, below Kyoto -->
                      <circle cx="376" cy="723" r="5" />
                      <line x1="371" y1="723" x2="284" y2="723" stroke="#c6a873" stroke-width="1.5" />
                      <text x="280" y="731" text-anchor="end">Osaka</text>
                    </g>
                  </svg>
                </div>
                <p class="map-hint">Click a region to explore experiential stays.</p>
              </div>

              <div class="map-body-right" id="mapBodyRight">
                <div v-if="!activeAreaInfo" class="map-default-msg" id="mapDefaultMsg">
                  <div class="map-default-hero">
                    <OptimizedImage
                      image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/9c5a64a9499340c1966fa97543ef6f1c/images_6f3aa139-3b09-4e65-a9ff-e3831fc3c4b0.jpeg"
                      alt="Curated Stay"
                      loading="eager"
                    />
                  </div>
                  <p class="map-default-label" style="margin-top: 18px">Destinations</p>
                  <h2 class="map-default-title">Find Your Curated Experiential Stay</h2>
                  <p class="map-area-desc">Discover a sanctuary where heritage meets modern comfort. Immerse yourself in spaces that tell a story, designed for the discerning traveler.</p>
                </div>
                <div v-else class="map-area-info" id="mapAreaInfo">
                  <div class="map-area-img-wrap">
                    <img
                      :src="getOptimizedImageUrl(activeAreaInfo.image)"
                      :alt="activeAreaInfo.title"
                      :style="{ objectPosition: activeAreaInfo.objectPosition ?? 'center center' }"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <p class="map-area-region">{{ activeAreaInfo.region }}</p>
                  <h2 class="map-area-title">{{ activeAreaInfo.title }}</h2>
                  <p class="map-area-desc">{{ activeAreaInfo.desc }}</p>
                  <div class="map-area-tags">
                    <span v-for="tag in activeAreaInfo.tags" :key="tag" class="map-area-tag">{{ tag }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobile: 選択地域の体験一覧。マップ本体の後ろに置き、選択後もマップを残したまま結果へスクロールできるようにする -->
            <div v-if="mobileSelectedRegionKey" class="mob-exp-section">
              <p class="mob-exp-label">{{ AREA_INFO[mobileSelectedRegionKey].title }}</p>
              <div v-if="regionLoading" class="mob-exp-loading">
                <span class="loading-dots">Loading…</span>
              </div>
              <template v-else>
                <p v-if="mobilePagedExperiences.length === 0" class="mob-exp-empty">More stays coming soon for this region.</p>
                <div v-else class="mob-exp-grid">
                  <template v-for="(exp, i) in mobilePagedExperiences" :key="exp.slug ?? exp.name ?? i">
                    <NuxtLink class="map-stay-card" :to="exp.slug ? `/experiences/${exp.slug}/` : exp.url">
                      <OptimizedImage :image-url="exp.images?.[0]?.imageUrl ?? exp.image" :alt="exp.name" loading="lazy" />
                      <div class="map-stay-card-body">
                        <p class="map-stay-card-name">{{ exp.name }}</p>
                        <p class="map-stay-card-loc">{{ exp.tags?.find((t: any) => t.tag?.tagGroupType === 'destination')?.tag?.name ?? exp.location }}</p>
                      </div>
                    </NuxtLink>
                  </template>
                </div>
                <div v-if="hasMobileMoreStays" class="map-stays-view-more">
                  <button type="button" class="map-stays-view-more-btn" @click="loadMoreMobileStays">View More</button>
                </div>
              </template>
            </div>

            <div class="map-stays-section" id="mapStaysSection" style="display: none" :class="{ 'mob-region-active': mobileSelectedRegionKey }">
              <p class="map-stays-label" id="mapStaysLabel">{{ mapStaysLabel }}</p>
              <div class="map-stays-grid" id="mapStaysGrid">
                <p v-if="!displayedExperiences || displayedExperiences.length === 0" class="map-stays-empty">More stays coming soon for this region.</p>
                <div v-else class="map-stays-list">
                  <template v-for="(exp, i) in pagedExperiences" :key="exp.slug ?? exp.name ?? i">
                    <div
                      v-if="exp.slug"
                      class="map-stay-card-wrapper"
                      style="cursor: pointer"
                      @click="
                        (e) => {
                          if (!e.defaultPrevented) openExternal(`/experiences/${exp.slug}/`);
                        }
                      "
                    >
                      <ExperienceCard :experience="exp" theme="dark" orientation="horizontal" :disable-carousel-on-sp="true" />
                    </div>
                    <NuxtLink v-else class="map-stay-card" :to="exp.url">
                      <OptimizedImage :image-url="exp.image" :alt="exp.name" loading="lazy" />
                      <div class="map-stay-card-body">
                        <p class="map-stay-card-name">{{ exp.name }}</p>
                        <p class="map-stay-card-loc">{{ exp.location }}</p>
                      </div>
                    </NuxtLink>
                  </template>
                </div>
                <div v-if="hasMoreStays" class="map-stays-view-more">
                  <button type="button" class="map-stays-view-more-btn" @click="loadMoreStays">View More</button>
                </div>
              </div>
            </div>
          </div>
          <div id="themesPanel" style="display: none">
            <div class="theme-tab-panel" id="themeTabPanel">
              <div v-if="themeTabs.length > 0" class="theme-tab-grid">
                <a
                  v-for="tab in themeTabs"
                  :key="tab.webSectionId"
                  href="#themeStaysPanel"
                  class="theme-tab-card"
                  :class="{ 'is-active': selectedThemeId === tab.webSectionId }"
                  @click.prevent="selectThemeTab(tab)"
                >
                  <OptimizedImage :image-url="tab.image" :alt="tab.title" loading="lazy" />
                  <div class="theme-tab-overlay">
                    <h3>{{ tab.title }}</h3>
                  </div>
                </a>
              </div>
              <!-- セクション未投入・取得失敗でタブが 0 件になっても、パネルが無言の空欄にならないようにする -->
              <div v-else-if="themeSections?.isLoading?.value" class="stay-list-loading">
                <span class="loading-dots">Loading…</span>
              </div>
              <p v-else class="map-stays-empty">More themes coming soon.</p>
            </div>

            <!-- 選択中のセクションが管理画面の削除・非公開や通貨切替で消えたとき、見出しも一覧も無いパネルを残さない -->
            <div v-if="selectedThemeTab" class="theme-stays-panel" id="themeStaysPanel">
              <div class="map-stays-section theme-map-stays-section">
                <p class="map-stays-label">{{ selectedThemeTitle }}</p>
                <div class="map-stays-grid">
                  <div v-if="themeSections?.isLoading?.value" class="stay-list-loading" style="grid-column: 1 / -1">
                    <span class="loading-dots">Loading…</span>
                  </div>
                  <!-- 体験 0 件のセクションを選んだとき、見出しだけのパネルにしない。地域一覧側と表現を揃える -->
                  <p v-else-if="pagedThemeExperiences.length === 0" class="map-stays-empty">More stays coming soon for this theme.</p>
                  <div v-else class="map-stays-list">
                    <!-- 同じ体験が同一セクションに二重登録されても描画が壊れないよう index を混ぜる -->
                    <template v-for="(exp, i) in pagedThemeExperiences" :key="`${i}-${exp.slug}`">
                      <div
                        class="map-stay-card-wrapper"
                        style="cursor: pointer"
                        @click="
                          (e) => {
                            if (!e.defaultPrevented) openExternal(`/experiences/${exp.slug}/`);
                          }
                        "
                      >
                        <ExperienceCard :experience="exp" theme="dark" orientation="horizontal" :disable-carousel-on-sp="true" />
                      </div>
                    </template>
                  </div>
                  <div v-if="hasMoreThemeStays" class="map-stays-view-more">
                    <button type="button" class="map-stays-view-more-btn" @click="loadMoreThemeStays">View More</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <section class="gallery-section section" id="gallery" aria-label="Gallery">
      <div class="container">
        <div class="gallery-head">
          <p class="gallery-label">Inside the Collection</p>
          <h2 class="gallery-title">Gallery</h2>
        </div>
      </div>

      <div class="gallery-slider" data-gallery-slider>
        <button class="gallery-nav gallery-nav--prev" type="button" aria-label="Previous slides">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M9 2L4 7L9 12" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="gallery-viewport">
          <div class="gallery-track" data-gallery-track>
            <figure v-for="(item, index) in verifiedGalleryItems" :key="`gallery-${index}`" class="gallery-slide">
              <NuxtLink v-if="item.link" :to="item.link" class="gallery-slide__link">
                <img :src="getOptimizedImageUrl(item.src)" :alt="item.alt || ''" :loading="index === 0 ? 'eager' : 'lazy'" decoding="async" />
              </NuxtLink>
              <img v-else :src="getOptimizedImageUrl(item.src)" :alt="item.alt || ''" :loading="index === 0 ? 'eager' : 'lazy'" decoding="async" />
            </figure>
          </div>
        </div>
        <button class="gallery-nav gallery-nav--next" type="button" aria-label="Next slides">
          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M5 2L10 7L5 12" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>

      <div class="gallery-footer">
        <button type="button" class="gallery-view-more-btn" data-gallery-open aria-haspopup="dialog" @click="trackGalleryViewMore">View More</button>
      </div>
    </section>

    <Teleport to="body">
      <div class="gallery-modal" id="galleryModal" hidden aria-hidden="true" aria-labelledby="galleryModalTitle">
        <div class="gallery-modal__panel" role="dialog" aria-modal="true">
          <div class="gallery-modal__head">
            <h3 class="gallery-modal__title" id="galleryModalTitle">Gallery</h3>
            <button type="button" class="gallery-modal__close" data-gallery-close aria-label="Close gallery">×</button>
          </div>
          <div class="gallery-modal__grid" data-gallery-modal-grid>
            <figure v-for="(item, index) in verifiedGalleryModalItems" :key="`gallery-modal-${index}`" class="gallery-modal__item">
              <NuxtLink v-if="item.link" :to="item.link" class="gallery-modal__link">
                <img :src="getOptimizedImageUrl(item.src)" :alt="item.alt || ''" loading="lazy" decoding="async" />
              </NuxtLink>
              <img v-else :src="getOptimizedImageUrl(item.src)" :alt="item.alt || ''" loading="lazy" decoding="async" />
            </figure>
          </div>
        </div>
      </div>
    </Teleport>

    <section class="story-section section">
      <div class="container">
        <div class="story-head">
          <div class="story-head-left">
            <h2 class="story-head-title">Wabunka Story</h2>
            <p class="story-head-sub">Meet the people behind the places.</p>
          </div>
        </div>

        <div class="story-grid">
          <template v-for="story in curatedStories" :key="story.id">
            <NuxtLink :to="`/stories/${story.id}/`" class="story-card" @click="trackStory(story.title)">
              <div class="story-card-media">
                <OptimizedImage :image-url="story.thumbnailImage.url" :alt="story.title" loading="lazy" />
                <div class="story-card-grad"></div>
                <div class="story-card-body">
                  <p class="story-card-label">{{ story.provider.name }}</p>
                  <h3 class="story-card-title">{{ story.title }}</h3>
                </div>
              </div>
            </NuxtLink>
          </template>
        </div>

        <div class="story-footer">
          <NuxtLink to="/stories/" class="story-find-btn"> Read the stories </NuxtLink>
        </div>
      </div>
    </section>

    <section class="reviews-section section">
      <div class="container">
        <div class="reviews-head">
          <h2 class="reviews-title">In Their Words</h2>
          <p class="reviews-subtitle">Guests from more than 110 countries have found a different kind of Japan through Wabunka.</p>
        </div>

        <div class="reviews-grid">
          <div class="review-card">
            <div class="review-card-top">
              <OptimizedImage
                image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/909ed712f1444a9cbc7da1eaef75db5e/itinerary-long13_PCKV01.jpg"
                alt="Kintsugi"
                class="review-img"
                loading="lazy"
              />
              <div class="review-meta">
                <p class="review-country">🇳🇿 New Zealand</p>
                <p class="review-experience">Kintsugi</p>
              </div>
            </div>
            <p class="review-text">
              A once in a lifetime chance to have a master of their craft teach you. You spend a day together learning so many new things, all while being immersed in Japanese culture.
            </p>
            <p class="review-author">E.M</p>
          </div>

          <div class="review-card">
            <div class="review-card-top">
              <OptimizedImage
                image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/43bc0fc75c11474a8edb9375fb91d94f/hiiragiya-geisha_PCKV.jpg"
                alt="Tea ceremony"
                class="review-img"
                loading="lazy"
              />
              <div class="review-meta">
                <p class="review-country">🇮🇹 Italy</p>
                <p class="review-experience">Tea ceremony &amp; Gastronomy</p>
              </div>
            </div>
            <p class="review-text">
              We loved the experience and couldn't recommend it more! It gave us a much deeper understanding of Japanese culture and helped us connect with the local community.
            </p>
            <p class="review-author">A.R</p>
          </div>

          <div class="review-card">
            <div class="review-card-top">
              <OptimizedImage
                image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/cfcfe77522054435b092c0b8e483e620/hiiragiya-atelier-shimura_PCKV.jpg"
                alt="Noh"
                class="review-img"
                loading="lazy"
              />
              <div class="review-meta">
                <p class="review-country">🇦🇺 Australia</p>
                <p class="review-experience">Noh</p>
              </div>
            </div>
            <p class="review-text">
              A unique experience, a lot of choices and well tailored to make it worth the cost. It was a chance to experience and understand more about traditional Japanese culture.
            </p>
            <p class="review-author">R.K</p>
          </div>

          <div class="review-card">
            <div class="review-card-top">
              <OptimizedImage
                image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/97d5154ce27341639dfb73e1ebdba9bf/en_hida-823kan-watanabe-shuzo_PCKV_new.jpg"
                alt="Sake tasting"
                class="review-img"
                loading="lazy"
              />
              <div class="review-meta">
                <p class="review-country">🇺🇸 United States</p>
                <p class="review-experience">Sake tasting</p>
              </div>
            </div>
            <p class="review-text">Everything is on high level with top ratings for the service. It is amazing experience! thank you!</p>
            <p class="review-author">A.W</p>
          </div>

          <div class="review-card">
            <div class="review-card-top">
              <OptimizedImage
                image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/ecf6469354d94fa79c041647e1ffc89f/wataya_PCKV%20(8).jpg"
                alt="Vegetarian cuisine"
                class="review-img"
                loading="lazy"
              />
              <div class="review-meta">
                <p class="review-country">🇨🇦 Canada</p>
                <p class="review-experience">Vegetarian cuisine</p>
              </div>
            </div>
            <p class="review-text">
              From the website, to the booking process, to the email communication of the translator, to the location of the teahouse, and to the actual experience — everything exceeded expectations.
            </p>
            <p class="review-author">A.C</p>
          </div>

          <div class="review-card">
            <div class="review-card-top">
              <OptimizedImage
                image-url="https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/8486dd3ca4aa463cbd8858f061047f82/ryokankurashiki_PCKV.jpg"
                alt="Fermentation"
                class="review-img"
                loading="lazy"
              />
              <div class="review-meta">
                <p class="review-country">🇺🇸 United States</p>
                <p class="review-experience">Fermentation</p>
              </div>
            </div>
            <p class="review-text">Everything about the experience exceeded expectations. From the teacher, to the interpreter, to learning about Kintsugi and its philosophy. Truly memorable.</p>
            <p class="review-author">K.D</p>
          </div>
        </div>
      </div>
    </section>

    <section class="section cta" id="cta" aria-label="Call to action">
      <div class="container">
        <div class="cta-inner">
          <h2>Request an Itinerary</h2>
          <p class="cta-lead">We design full itineraries connecting stays, experiences, and routes across regions. Tell us where you want to go and we will shape the journey.</p>
          <div class="cta-actions">
            <NuxtLink class="btn btn--cta-primary" to="/contact/" @click="trackItineraryCta">
              Request an itinerary
              <span aria-hidden="true">→</span>
            </NuxtLink>
            <a class="btn btn--cta-secondary" href="#map-interaction">
              Back to stays
              <span aria-hidden="true">↑</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style>
/* スコープ: <body class="curated-stay-page"> 配下のみに限定（useHead で当ページ表示中だけ付与）。
   元 pages/lp/curated-stay/curated-stay.css をインライン化し、全セレクタを body.curated-stay-page にゲート。
   .l-main / .l-footer / #jsi-footer 等の共有レイアウト上書きも当ページ表示中のみ有効。 */
body.curated-stay-page,
html:has(body.curated-stay-page) {
  background: #1a1a1a;
  color: #f4f1ed;
  margin: 0;
}
body.curated-stay-page .nav-link__icon svg {
  height: 16px;
  max-height: 16px;
  max-width: 16px;
  width: 16px;
}
body.curated-stay-page {
  --bg: #1a1a1a;
  --bg-card: #1a1a1a;
  --border: #f4f1ed;
  --border-accent: #c6a86c;
  --brown: #1a1a1a;
  --gold: #c6a86c;
  --cream: #f4f1ed;
  --ink: rgba(244, 241, 237, 0.92);
  --muted: rgba(244, 241, 237, 0.6);
  --cardShadow: 0 18px 50px rgba(0, 0, 0, 0.7);
  --text-h1-size: 36px;
  --text-h1-weight: 400;
  --text-h1-lh: 48px;
  --text-h1-ls: 0.5px;
  --text-h2-size: 26px;
  --text-h2-weight: 400;
  --text-h2-lh: 40px;
  --text-h2-ls: 0.5px;
  --text-h3-size: 22px;
  --text-h3-weight: 400;
  --text-h3-lh: 32px;
  --text-h3-ls: 0.5px;
  --text-p1-size: 16px;
  --text-p1-weight: 300;
  --text-p1-lh: 24px;
  --text-p2-size: 16px;
  --text-p2-weight: 300;
  --text-p2-lh: 20px;
  --radius: 0px;
  --radius-btn: 4px;
  --radius-modal: 8px;
  --motion-hover: 0.15s ease-out;
  --motion-transition: 0.3s ease-in-out;
  --page-gutter: 120px;
  --content-max: 1200px;
}
body.curated-stay-page * {
  box-sizing: border-box;
}
html:has(body.curated-stay-page) {
  overflow-x: hidden;
  scroll-behavior: smooth;
}
body.curated-stay-page {
  background: var(--bg);
  color: var(--ink);
  overflow-x: hidden;
  font-family: Spectral, serif;
  font-size: var(--text-p1-size);
  font-weight: var(--text-p1-weight);
  line-height: var(--text-p1-lh);
  margin: 0;
}
body.curated-stay-page img {
  display: block;
  max-width: 100%;
}
body.curated-stay-page h1,
body.curated-stay-page h2,
body.curated-stay-page h3 {
  font-family: Spectral, serif;
  margin: 0;
}
body.curated-stay-page h1 {
  font-size: var(--text-h1-size);
  font-weight: var(--text-h1-weight);
  letter-spacing: var(--text-h1-ls);
  line-height: var(--text-h1-lh);
}
body.curated-stay-page h2 {
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
}
body.curated-stay-page h3 {
  font-size: var(--text-h3-size);
  font-weight: var(--text-h3-weight);
  letter-spacing: var(--text-h3-ls);
  line-height: var(--text-h3-lh);
}
body.curated-stay-page p {
  font-size: var(--text-p1-size);
  font-weight: var(--text-p1-weight);
  line-height: var(--text-p1-lh);
  margin: 0;
}
body.curated-stay-page .text-p2 {
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
}
body.curated-stay-page a,
body.curated-stay-page button,
body.curated-stay-page nav {
  font-family: Spectral, serif;
}
body.curated-stay-page .container {
  margin: 0 auto;
  max-width: var(--content-max);
  padding: 0;
  width: min(var(--content-max), calc(100vw - var(--page-gutter) * 2));
}
body.curated-stay-page .site-header {
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.16), transparent);
  border-bottom: none;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 50;
}
body.curated-stay-page .site-header .nav {
  position: relative;
}
body.curated-stay-page .site-header__inner {
  align-items: center;
  display: flex;
  gap: 8px;
  height: 70px;
  justify-content: space-between;
  margin: 0 auto;
  max-width: 1440px;
  padding: 0 var(--page-gutter);
  width: min(1440px, 100%);
}
body.curated-stay-page .logo {
  align-items: center;
  color: var(--cream);
  display: inline-flex;
  flex-shrink: 0;
  text-decoration: none;
}
body.curated-stay-page .logo__img {
  display: block;
  height: 20px;
  width: auto;
}
body.curated-stay-page .nav {
  display: flex;
  justify-content: flex-end;
  margin-left: auto;
}
body.curated-stay-page .nav,
body.curated-stay-page .nav-link {
  align-items: center;
  gap: 8px;
}
body.curated-stay-page .nav-link {
  border-radius: 0;
  color: var(--cream);
  display: inline-flex;
  font-family: Spectral, serif;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.02em;
  line-height: 1.2;
  padding: 8px;
  text-decoration: none;
  transition: opacity var(--motion-hover);
  white-space: nowrap;
}
body.curated-stay-page .nav-link:hover {
  background: transparent;
  color: var(--cream);
  opacity: 0.82;
}
body.curated-stay-page .nav-link__icon {
  color: inherit;
  display: inline-flex;
  flex-shrink: 0;
  height: 16px;
  width: 16px;
}
body.curated-stay-page .nav-link__icon svg {
  display: block;
  height: 100%;
  width: 100%;
}
body.curated-stay-page .nav-toggle {
  align-items: center;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  color: var(--cream);
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 6px;
  height: 40px;
  justify-content: center;
  margin: 0;
  padding: 0;
  width: 40px;
}
body.curated-stay-page .nav-toggle__bar {
  background: currentColor;
  display: block;
  height: 1px;
  transition:
    transform var(--motion-hover),
    opacity var(--motion-hover);
  width: 22px;
}
body.curated-stay-page .nav-drawer {
  background: #00000059;
  display: flex;
  inset: 0;
  justify-content: flex-end;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  transition:
    opacity var(--motion-transition),
    visibility var(--motion-transition);
  visibility: hidden;
  z-index: 100;
}
body.curated-stay-page.nav-open .nav-drawer {
  opacity: 1;
  pointer-events: auto;
  visibility: visible;
}
body.curated-stay-page.nav-open {
  overflow: hidden;
}
body.curated-stay-page .nav-drawer__inner {
  background: #fff;
  height: 100%;
  margin: 0;
  overflow-y: auto;
  padding: 72px 24px 48px;
  position: relative;
  width: min(390px, 100vw);
  -webkit-overflow-scrolling: touch;
  box-shadow: -4px 0 24px #0000001f;
  transform: translate(100%);
  transition: transform var(--motion-transition);
}
body.curated-stay-page.nav-open .nav-drawer__inner {
  transform: translate(0);
}
body.curated-stay-page .nav-drawer__close {
  align-items: center;
  background: transparent;
  border: none;
  color: #1a1a1a;
  cursor: pointer;
  display: inline-flex;
  height: 44px;
  justify-content: center;
  padding: 0;
  position: absolute;
  right: 20px;
  top: 20px;
  width: 44px;
}
body.curated-stay-page .nav-drawer__close svg {
  display: block;
  height: 20px;
  width: 20px;
}
body.curated-stay-page .nav-drawer__menu {
  list-style: none;
  margin: 0;
  padding: 0;
}
body.curated-stay-page .nav-drawer__divider {
  border: none;
  border-top: 1px solid #e5e5e5;
  margin: 8px 0 16px;
}
body.curated-stay-page .nav-drawer__item {
  align-items: center;
  color: #1a1a1a;
  display: flex;
  font-family: Spectral, serif;
  font-size: 17px;
  font-weight: 400;
  gap: 16px;
  justify-content: space-between;
  letter-spacing: 0.01em;
  line-height: 1.35;
  padding: 18px 0;
  text-decoration: none;
  transition: opacity var(--motion-hover);
  width: 100%;
}
body.curated-stay-page .nav-drawer__item:hover {
  opacity: 0.65;
}
body.curated-stay-page .nav-drawer__item-main {
  align-items: center;
  display: inline-flex;
  gap: 16px;
  min-width: 0;
}
body.curated-stay-page .nav-drawer__icon {
  color: #1a1a1a;
  display: inline-flex;
  flex-shrink: 0;
  height: 22px;
  width: 22px;
}
body.curated-stay-page .nav-drawer__icon svg {
  display: block;
  height: 100%;
  width: 100%;
}
body.curated-stay-page .nav-drawer__chevron {
  color: #1a1a1a;
  display: inline-flex;
  flex-shrink: 0;
  height: 10px;
  opacity: 0.85;
  width: 10px;
}
body.curated-stay-page .nav-drawer__chevron svg {
  display: block;
  height: 100%;
  width: 100%;
}
body.curated-stay-page .nav-drawer__follow {
  align-items: center;
  color: #1a1a1a;
  display: flex;
  font-family: Spectral, serif;
  font-size: 17px;
  font-weight: 400;
  gap: 16px;
  justify-content: space-between;
  line-height: 1.35;
  padding: 18px 0 8px;
}
body.curated-stay-page .nav-drawer__social {
  align-items: center;
  display: inline-flex;
  gap: 20px;
}
body.curated-stay-page .nav-drawer__social a {
  color: #1a1a1a;
  display: inline-flex;
  transition: opacity var(--motion-hover);
}
body.curated-stay-page .nav-drawer__social a:hover {
  opacity: 0.65;
}
body.curated-stay-page .nav-drawer__social svg {
  display: block;
  height: 22px;
  width: 22px;
}
body.curated-stay-page .hero {
  background: var(--brown);
  display: block;
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  padding-top: 0;
  position: relative;
}
body.curated-stay-page .hero__slideshow {
  inset: 0;
  overflow: hidden;
  position: fixed;
  z-index: 0;
}
body.curated-stay-page .hero__slide {
  inset: 0;
  opacity: 0;
  position: absolute;
  transition: opacity 2.2s cubic-bezier(0.33, 1, 0.68, 1);
  will-change: opacity;
  z-index: 0;
}
body.curated-stay-page .hero__slide.is-active {
  opacity: 1;
  z-index: 2;
}
body.curated-stay-page .hero__slide.is-leaving {
  opacity: 0;
  z-index: 1;
}
body.curated-stay-page .hero__slide img {
  display: block;
  filter: brightness(0.88);
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  -o-object-position: center center;
  object-position: center center;
  transform: translate3d(-2.4%, 0, 0) scale(1.08);
  transition:
    transform 7.2s linear,
    filter var(--motion-transition);
  width: 100%;
  will-change: transform;
}
body.curated-stay-page .hero__slide.is-active img {
  transform: translate3d(2.4%, 0, 0) scale(1.08);
}
body.curated-stay-page .hero__slide.is-leaving img {
  transform: translate3d(2.8%, 0, 0) scale(1.08);
  transition-duration: 2.2s, var(--motion-transition);
}
body.curated-stay-page .hero__slideshow:after {
  background: #0003;
  z-index: 1;
}
body.curated-stay-page .hero:after,
body.curated-stay-page .hero__slideshow:after {
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
}
body.curated-stay-page .hero:after {
  background: linear-gradient(180deg, #00000026, #00000052 45%, #000000ad);
  z-index: 2;
}
@keyframes kv-text-in {
  0% {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes kv-scroll-in {
  0% {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
body.curated-stay-page .hero-inner .hero-actions .btn,
body.curated-stay-page .hero-inner .kv-copy,
body.curated-stay-page .hero-inner .kv-side p {
  animation: kv-text-in 1s ease-out forwards;
  opacity: 0;
}
body.curated-stay-page .hero-inner .kv-copy {
  animation-delay: 0.4s;
}
body.curated-stay-page .hero-inner .kv-side p {
  animation-delay: 0.62s;
}
body.curated-stay-page .hero-inner .hero-actions .btn:first-child {
  animation-delay: 0.84s;
}
body.curated-stay-page .hero-inner .hero-actions .btn:nth-child(2) {
  animation-delay: 1s;
}
body.curated-stay-page .hero .kv-scroll {
  animation:
    kv-scroll-in 0.85s ease-out 1.18s forwards,
    kv-float 2.35s ease-in-out 2.35s infinite;
  opacity: 0;
}
body.curated-stay-page .hero-inner {
  align-items: flex-end;
  bottom: 0;
  display: grid;
  gap: 60px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  left: 50%;
  margin: 0;
  max-width: var(--content-max);
  padding: 0 0 160px;
  position: absolute;
  transform: translate(-50%);
  width: min(var(--content-max), calc(100vw - var(--page-gutter) * 2));
  z-index: 3;
}
body.curated-stay-page .eyebrow {
  align-items: center;
  color: #f4f1ede6;
  display: inline-flex;
  font-size: 13px;
  gap: 10px;
  letter-spacing: 0.18em;
  margin-bottom: 16px;
  text-transform: uppercase;
}
body.curated-stay-page .eyebrow:before {
  background: linear-gradient(90deg, #c6a86cf2, #c6a86c40);
  content: '';
  height: 1px;
  width: 44px;
}
body.curated-stay-page .hero h1 {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h1-size);
  font-style: normal;
  font-weight: var(--text-h1-weight);
  letter-spacing: var(--text-h1-ls);
  line-height: var(--text-h1-lh);
  max-width: 580px;
  overflow-wrap: normal;
  white-space: normal;
  word-break: normal;
}
body.curated-stay-page .hero .kv-copy h1 {
  font-size: 44.66667px;
  font-weight: 300;
  letter-spacing: 0.02em;
  line-height: 1.15;
  max-width: 720px;
}
body.curated-stay-page .kv-copy,
body.curated-stay-page .kv-side {
  min-width: 0;
}
body.curated-stay-page .kv-side {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-left: auto;
  max-width: 420px;
  padding-left: 128px;
}
body.curated-stay-page .kv-side p {
  color: var(--muted);
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 0;
  max-width: 280px;
  text-align: left;
}
body.curated-stay-page .kv-side .hero-actions {
  justify-content: flex-start;
}
body.curated-stay-page .hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 32px;
}
body.curated-stay-page .hero-actions .btn {
  flex: none;
  width: 280px;
}
body.curated-stay-page .btn {
  align-items: center;
  border-radius: var(--radius-btn);
  cursor: pointer;
  display: inline-flex;
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  gap: 10px;
  justify-content: center;
  letter-spacing: 0.02em;
  line-height: var(--text-p2-lh);
  padding: 14px 28px;
  text-decoration: none;
  transition:
    background var(--motion-hover),
    border-color var(--motion-hover),
    color var(--motion-hover);
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
  white-space: nowrap;
}
body.curated-stay-page .btn--cream {
  background: #f4f1ed14;
  border: 1px solid rgba(244, 241, 237, 0.35);
  color: #f4f1edf2;
}
body.curated-stay-page .btn--cream:hover {
  background: #f4f1ed1f;
  border-color: #c6a86c80;
}
body.curated-stay-page .btn--gold {
  background: var(--gold);
  border: 1px solid var(--border-accent);
  box-shadow: 0 14px 40px #c6a86c38;
  color: var(--bg);
}
body.curated-stay-page .btn--gold:hover {
  background: #d4b87a;
  border-color: #d4b87a;
}
body.curated-stay-page .hero-gallery {
  align-self: stretch;
  display: grid;
  gap: 14px;
  grid-auto-rows: 140px;
  grid-template-columns: repeat(2, 1fr);
}
body.curated-stay-page .hero-tile {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.18);
  border-radius: var(--radius);
  overflow: hidden;
  position: relative;
}
body.curated-stay-page .hero-tile img {
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transform: scale(1.02);
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .hero-tile:hover img {
  transform: scale(1.09);
}
body.curated-stay-page .hero-tile.tile-1 {
  grid-column: 1/-1;
  grid-row: 1 / span 2;
}
body.curated-stay-page .hero-tile.tile-2 {
  grid-column: 1;
}
body.curated-stay-page .hero-tile.tile-3 {
  grid-column: 2;
}
body.curated-stay-page .kv-scroll {
  align-items: center;
  background: #1a1a1aa6;
  border: 1px solid hsla(40, 44%, 60%, 0.42);
  border-radius: var(--radius-btn);
  bottom: 60px;
  color: #f4f1edf2;
  display: inline-flex;
  height: 58px;
  justify-content: center;
  left: 50%;
  position: absolute;
  text-decoration: none;
  transform: translate(-50%);
  transition:
    background var(--motion-hover),
    border-color var(--motion-hover);
  width: 58px;
  z-index: 4;
}
body.curated-stay-page .kv-scroll:hover {
  background: #c6a86c33;
  border-color: #c6a86cbf;
}
body.curated-stay-page .kv-scroll svg {
  height: 22px;
  width: 22px;
}
@keyframes kv-float {
  0%,
  to {
    transform: translate(-50%) translateY(0);
  }
  50% {
    transform: translate(-50%) translateY(-10px);
  }
}
body.curated-stay-page .section {
  background: var(--bg);
  padding: 86px 0;
  position: relative;
  z-index: 5;
}
body.curated-stay-page .section--tight {
  padding: 66px 0;
}
body.curated-stay-page .section-head {
  align-items: baseline;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 34px;
}
body.curated-stay-page .section-head h2 {
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
}
body.curated-stay-page .section-head p {
  color: var(--muted);
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 0;
  max-width: 52ch;
}
body.curated-stay-page .philosophy {
  background: #1a1a1a;
}
body.curated-stay-page .philosophy.section {
  padding-bottom: 0;
}
body.curated-stay-page .philosophy + .iconic-stays-section {
  margin-top: 0;
}
body.curated-stay-page .intro-cards-head {
  margin: 0 auto 100px;
  max-width: 900px;
  text-align: center;
}
body.curated-stay-page .intro-cards-head h2 {
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin-left: auto;
  margin-right: auto;
  max-width: 600px;
  text-align: center;
}
body.curated-stay-page .intro-cards-head .lead {
  color: var(--muted);
  font-size: var(--text-p1-size);
  font-weight: var(--text-p1-weight);
  line-height: var(--text-p1-lh);
  margin: 20px auto 0;
  max-width: 76ch;
}
body.curated-stay-page .intro-card-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
body.curated-stay-page .intro-card {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.18);
  border-radius: 0;
  box-shadow: 0 18px 50px #0006;
  cursor: default;
  overflow: hidden;
  pointer-events: none;
}
body.curated-stay-page .intro-card__media {
  background: #ffffff05;
  border-radius: 0;
  height: 320px;
  overflow: hidden;
}
body.curated-stay-page .intro-card__media img {
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
}
body.curated-stay-page .intro-card .intro-card__media img {
  transform: none !important;
  transition: none !important;
}
body.curated-stay-page .intro-card__body {
  padding: 22px 20px 24px;
}
body.curated-stay-page .intro-card__body h3 {
  color: var(--cream);
  font-size: var(--text-h3-size);
  font-weight: var(--text-h3-weight);
  letter-spacing: var(--text-h3-ls);
  line-height: var(--text-h3-lh);
}
body.curated-stay-page .intro-card__body p {
  color: var(--muted);
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 14px 0 0;
}
body.curated-stay-page .stays {
  background: radial-gradient(900px 380px at 20% 0, hsla(40, 44%, 60%, 0.18), transparent 55%);
}
body.curated-stay-page .stay-grid {
  display: grid;
  gap: 22px;
}
body.curated-stay-page .stay-card {
  background: #000;
  border: 1px solid rgba(244, 241, 237, 0.08);
  border-radius: var(--radius);
  box-shadow: var(--cardShadow);
  overflow: hidden;
  position: relative;
  transform: translateZ(0);
}
body.curated-stay-page .reveal {
  opacity: 0;
  transform: translateY(22px);
  transition:
    opacity 0.9s ease-out,
    transform 0.9s ease-out;
  transition-delay: var(--reveal-delay, 0s);
}
body.curated-stay-page .reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}
body.curated-stay-page .stay-media {
  height: 60vh;
  min-height: 60vh;
  width: 100%;
}
body.curated-stay-page .stay-media img {
  filter: saturate(1.02) contrast(1.03);
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transform: scale(1.04);
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .stay-overlay {
  align-items: flex-end;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.68) 35%, rgba(0, 0, 0, 0.78));
  display: flex;
  gap: 16px;
  inset: auto 0 0;
  justify-content: space-between;
  padding: 18px 18px 16px;
  position: absolute;
}
body.curated-stay-page .stay-title {
  color: var(--cream);
  font-size: var(--text-h3-size);
  font-weight: var(--text-h3-weight);
  letter-spacing: var(--text-h3-ls);
  line-height: var(--text-h3-lh);
}
body.curated-stay-page .stay-location {
  color: #f4f1edc7;
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  letter-spacing: 0.02em;
  line-height: var(--text-p2-lh);
  margin-top: 6px;
}
body.curated-stay-page .stay-arrow {
  align-items: center;
  background: #1a1a1a2e;
  border: 1px solid rgba(244, 241, 237, 0.35);
  border-radius: var(--radius-btn);
  color: #f4f1edf2;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 46px;
  justify-content: center;
  position: relative;
  transition:
    border-color var(--motion-hover),
    background var(--motion-hover),
    color var(--motion-hover);
  width: 46px;
}
body.curated-stay-page .stay-arrow svg {
  height: 22px;
  transition: transform var(--motion-hover);
  width: 22px;
}
body.curated-stay-page .stay-card:hover .stay-media img {
  transform: scale(1.12);
}
body.curated-stay-page .stay-card:hover .stay-arrow {
  background: var(--gold);
  border-color: var(--border-accent);
  color: var(--bg);
}
body.curated-stay-page .stay-card:hover .stay-arrow svg {
  transform: translate(2px);
}
body.curated-stay-page .features {
  background: #1a1a1a;
}
body.curated-stay-page .feature-row {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, 1fr);
}
body.curated-stay-page .feature-card {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.2);
  border-radius: var(--radius);
  min-height: 190px;
  padding: 22px 18px;
}
body.curated-stay-page .feature-card h3 {
  color: var(--cream);
  font-size: var(--text-h3-size);
  font-weight: var(--text-h3-weight);
  letter-spacing: var(--text-h3-ls);
  line-height: var(--text-h3-lh);
}
body.curated-stay-page .feature-card p {
  color: var(--muted);
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 12px 0 0;
}
body.curated-stay-page .feature-card:after {
  background: linear-gradient(90deg, hsla(40, 44%, 60%, 0.7), hsla(40, 44%, 60%, 0.12), transparent);
  content: '';
  display: block;
  height: 1px;
  margin-top: 18px;
}
body.curated-stay-page .cta.section {
  align-items: center;
  background-color: #1a1a1a;
  background-image: url(https://images.microcms-assets.io/assets/a061b71b7a084ccfa781f19d8659cbbd/68931a6e51394faea44ed4772928d230/gokayama.png);
  background-position: 50%;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  min-height: 560px;
  padding: 120px 0;
  position: relative;
}
body.curated-stay-page .cta:before {
  background: #0000008c;
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
}
body.curated-stay-page .cta > .container {
  position: relative;
  width: 100%;
  z-index: 1;
}
body.curated-stay-page .cta-inner {
  background: transparent;
  border: none;
  box-shadow: none;
  margin: 0 auto;
  max-width: 640px;
  padding: 0;
  text-align: center;
}
body.curated-stay-page .cta-label {
  color: #c6a86c;
  font-size: 11px;
  letter-spacing: 0.14em;
  margin: 0 0 20px;
  text-transform: uppercase;
}
body.curated-stay-page .cta-inner h2 {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: clamp(28px, 3.6vw, 48px);
  font-weight: 300;
  letter-spacing: 0.02em;
  line-height: 1.2;
  margin: 0;
  white-space: nowrap;
}
body.curated-stay-page .cta-inner {
  padding: 0 var(--page-gutter);
}
body.curated-stay-page .cta-inner .cta-lead {
  color: #ffffffc7;
  font-size: var(--text-p1-size);
  font-weight: var(--text-p1-weight);
  line-height: 1.85;
  margin: 28px auto 0;
  max-width: 52ch;
}
body.curated-stay-page .cta-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  justify-content: center;
  margin-top: 40px;
}
body.curated-stay-page .cta-actions .btn {
  width: 280px;
}
body.curated-stay-page .cta-actions .btn--cta-primary {
  background: transparent;
  border: 1px solid hsla(40, 44%, 60%, 0.85);
  box-shadow: none;
  color: #c6a86c;
  letter-spacing: 0.12em;
}
body.curated-stay-page .cta-actions .btn--cta-primary:hover {
  background: #c6a86c1f;
  border-color: #c6a86c;
  color: #d4b87a;
}
body.curated-stay-page .cta-actions .btn--cta-secondary {
  background: transparent;
  border: 1px solid rgba(244, 241, 237, 0.35);
  color: #f4f1ede6;
  letter-spacing: 0.1em;
}
body.curated-stay-page .cta-actions .btn--cta-secondary:hover {
  background: #f4f1ed14;
  border-color: #f4f1ed8c;
}
@media (max-width: 820px) {
  body.curated-stay-page .cta.section {
    min-height: 480px;
    padding: 88px 0;
  }
  body.curated-stay-page .cta-inner .cta-lead {
    font-size: 15px;
    line-height: 1.75;
  }
}
body.curated-stay-page .map-interaction-section {
  background: #1a1a1a;
  color: #ffffffdb;
  padding-bottom: 0;
  padding-top: 100px;
}
body.curated-stay-page .map-interaction-section > .container {
  display: block;
  max-width: var(--content-max);
  text-align: start;
  width: min(var(--content-max), calc(100vw - var(--page-gutter) * 2));
}
body.curated-stay-page .map-tabs {
  border-bottom: 1px solid hsla(40, 44%, 60%, 0.25);
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 32px;
}
body.curated-stay-page .map-tab {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: #ffffff73;
  cursor: pointer;
  font-family: Spectral, serif;
  font-size: 15px;
  letter-spacing: 0.06em;
  margin-bottom: -1px;
  padding: 18px 0;
  text-align: center;
  transition:
    color var(--motion-hover),
    border-color var(--motion-hover);
  width: 100%;
}
body.curated-stay-page .map-tab:hover {
  color: #ffffffbf;
}
body.curated-stay-page .map-tab.is-active {
  background: transparent;
  border-bottom: 2px solid var(--border-accent);
  color: #fffffff2;
  font-weight: var(--text-h2-weight);
}
body.curated-stay-page .map-hint {
  color: #ffffff8c;
  font-size: 13px;
  line-height: 1.8;
  margin: 16px 0 0;
}
body.curated-stay-page footer {
  background: var(--brown);
  border-top: 1px solid hsla(40, 44%, 60%, 0.2);
  color: #f4f1ede0;
  padding: 34px 0 44px;
  position: relative;
  z-index: 5;
}
body.curated-stay-page .footer-inner {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  justify-content: space-between;
}
body.curated-stay-page .footer-note {
  color: #f4f1edb8;
  font-size: 13px;
  line-height: 1.8;
  margin: 0;
}
body.curated-stay-page .footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}
body.curated-stay-page .footer-links a {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.2);
  border-radius: var(--radius-btn);
  color: #f4f1edd9;
  font-size: 13px;
  padding: 10px;
  text-decoration: none;
  transition: border-color var(--motion-hover);
}
body.curated-stay-page .footer-links a:hover {
  border-color: #c6a86c59;
}
body.curated-stay-page .theme-grid-section {
  margin: 0 0 28px;
  padding: 0;
  width: 100%;
}
body.curated-stay-page .theme-grid {
  aspect-ratio: 16/9;
  display: grid;
  gap: 4px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}
body.curated-stay-page .theme-card {
  color: #fff;
  cursor: pointer;
  display: block;
  overflow: hidden;
  position: relative;
  text-decoration: none;
}
body.curated-stay-page .theme-card:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 3px;
  z-index: 1;
}
body.curated-stay-page .theme-card img {
  display: block;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .theme-card:hover img {
  transform: scale(1.05);
}
body.curated-stay-page .theme-card-overlay {
  align-items: center;
  background: #0000007a;
  color: #fff;
  display: flex;
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    Segoe UI,
    sans-serif;
  inset: 0;
  justify-content: center;
  padding: 18px 22px;
  position: absolute;
  text-align: center;
}
body.curated-stay-page .theme-card-title {
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 0;
  max-width: 38ch;
}
body.curated-stay-page .theme-card-option {
  display: inline;
  font-size: 11px;
  font-weight: var(--text-p2-weight);
  letter-spacing: 0.1em;
  margin-right: 0.35em;
  opacity: 0.88;
  text-transform: uppercase;
}
body.curated-stay-page .stay-list-section[id] {
  scroll-margin-top: 96px;
}
@media (max-width: 768px) {
  body.curated-stay-page .theme-grid {
    aspect-ratio: unset;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(4, minmax(200px, 1fr));
  }
}
body.curated-stay-page .stay-list-section {
  background-color: transparent;
  border-top: 1px solid hsla(40, 44%, 60%, 0.14);
  margin-bottom: 64px;
  padding: 56px 0 0;
  width: 100%;
}
body.curated-stay-page .stay-grid + .stay-list-section {
  margin-top: 8px;
}
body.curated-stay-page .stay-list-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h3-size);
  font-weight: var(--text-h3-weight);
  letter-spacing: var(--text-h3-ls);
  line-height: var(--text-h3-lh);
  margin: 0 0 28px;
}
body.curated-stay-page .stay-list-grid,
body.curated-stay-page .stay-list-section .stay-list-grid {
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(2, 1fr);
  width: 100%;
}
body.curated-stay-page .stay-list-card,
body.curated-stay-page .stay-list-section .stay-list-card {
  aspect-ratio: 16/9;
  border-radius: 0;
  display: block;
  overflow: hidden;
  position: relative;
  text-decoration: none;
}
body.curated-stay-page .stay-list-section .stay-list-card {
  background: #000;
  border: none;
  box-shadow: none;
  color: #fff;
  transform: none;
}
body.curated-stay-page .stay-list-section .stay-list-card:focus-visible {
  outline: 2px solid var(--gold);
  outline-offset: 3px;
  z-index: 1;
}
body.curated-stay-page .stay-list-section .stay-list-card img {
  display: block;
  filter: none;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transform: none;
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .stay-list-section .stay-list-card:hover img {
  transform: scale(1.05);
}
body.curated-stay-page .stay-list-section .stay-card-overlay {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.75) 0, transparent);
  bottom: 0;
  color: #fff;
  font-family:
    ui-sans-serif,
    system-ui,
    -apple-system,
    Segoe UI,
    sans-serif;
  left: 0;
  padding: 20px;
  position: absolute;
  right: 0;
}
body.curated-stay-page .stay-list-section .stay-card-title {
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  margin: 0;
}
@media (max-width: 768px) {
  body.curated-stay-page .stay-list-section .stay-list-grid {
    grid-template-columns: 1fr;
  }
  body.curated-stay-page .stay-list-section {
    padding: 40px 0 0;
  }
}
@media (max-width: 980px) {
  /* position: fixed だと iOS Safari のスクロール/ズーム時に hero 外へはみ出すため absolute に変更 */
  body.curated-stay-page .hero__slideshow {
    position: absolute;
  }
  body.curated-stay-page .hero-inner {
    bottom: 0;
    gap: 24px;
    grid-template-columns: 1fr;
    padding-bottom: 140px;
    transform: translate(-50%);
    width: min(var(--content-max), calc(100vw - var(--page-gutter) * 2));
  }
  body.curated-stay-page .kv-copy,
  body.curated-stay-page .kv-side {
    margin-left: 0;
    max-width: 100%;
  }
  body.curated-stay-page .kv-side {
    align-items: stretch;
    padding-left: 0;
  }
  body.curated-stay-page .section {
    padding: 72px 0;
  }
  body.curated-stay-page .section-head {
    align-items: flex-start;
    flex-direction: column;
  }
  body.curated-stay-page .feature-row,
  body.curated-stay-page .intro-card-grid {
    grid-template-columns: 1fr;
  }
  body.curated-stay-page .stay-media {
    height: 60vh;
    min-height: 60vh;
  }
}
@media (max-width: 820px) {
  body.curated-stay-page {
    --page-gutter: 16px;
  }
  body.curated-stay-page .site-header__inner {
    padding: 0 var(--page-gutter);
  }
  body.curated-stay-page .nav {
    gap: 4px;
  }
  body.curated-stay-page .nav-link span:not(.nav-link__icon) {
    display: none;
  }
  body.curated-stay-page .nav-link {
    padding: 8px 6px;
  }
  body.curated-stay-page .hero {
    padding-top: 0;
  }
  body.curated-stay-page .hero-inner {
    padding-bottom: 120px;
  }
  body.curated-stay-page .nav-drawer__inner {
    padding: 68px 20px 40px;
  }
  body.curated-stay-page .nav-drawer__close {
    right: 16px;
    top: 16px;
  }
  body.curated-stay-page .nav-drawer__item {
    font-size: 16px;
    padding: 16px 0;
  }
  body.curated-stay-page .hero .kv-copy h1 {
    font-size: 28px;
    line-height: 1.2;
  }
  body.curated-stay-page .iconic-stays-section {
    padding-top: 80px !important;
  }
  body.curated-stay-page .map-interaction-section {
    padding-bottom: 0 !important;
  }
  body.curated-stay-page .gallery-section {
    padding-top: 80px !important;
  }
  body.curated-stay-page .hero-actions {
    align-items: stretch;
    flex-direction: column;
  }
  body.curated-stay-page .hero-actions .btn {
    flex: none;
    white-space: normal;
    width: 100%;
  }
  body.curated-stay-page .cta-actions {
    align-items: stretch;
    flex-direction: column;
  }
  body.curated-stay-page .cta-actions .btn {
    font-size: var(--text-p2-size);
    letter-spacing: 0.02em;
    white-space: normal;
    width: 100%;
  }
  body.curated-stay-page .story-find-btn,
  body.curated-stay-page .gallery-view-more-btn,
  body.curated-stay-page .map-stays-view-more-btn {
    font-size: var(--text-p2-size);
    letter-spacing: 0.02em;
  }
  body.curated-stay-page .kv-scroll {
    bottom: 24px;
  }
}
@media (prefers-reduced-motion: reduce) {
  html:has(body.curated-stay-page) {
    scroll-behavior: auto;
  }
  body.curated-stay-page .reveal {
    opacity: 1;
    transform: none;
  }
  body.curated-stay-page .hero-tile img,
  body.curated-stay-page .reveal,
  body.curated-stay-page .stay-media img {
    transition: none;
  }
  body.curated-stay-page .kv-scroll {
    animation: none;
  }
  body.curated-stay-page .nav-drawer,
  body.curated-stay-page .nav-drawer__inner {
    transition: none;
  }
  body.curated-stay-page.nav-open .nav-drawer__inner {
    transform: none;
  }
  body.curated-stay-page .hero__slide,
  body.curated-stay-page .hero__slide img {
    transform: none;
    transition: none;
  }
  body.curated-stay-page .hero__slide.is-active {
    opacity: 1;
  }
  body.curated-stay-page .hero__slide:not(.is-active) {
    opacity: 0;
  }
  body.curated-stay-page .hero__slide img {
    filter: none;
  }
  body.curated-stay-page .hero .kv-scroll,
  body.curated-stay-page .hero-inner .hero-actions .btn,
  body.curated-stay-page .hero-inner .kv-copy,
  body.curated-stay-page .hero-inner .kv-side p {
    animation: none;
    opacity: 1;
    transform: none;
  }
  body.curated-stay-page .hero .kv-scroll {
    transform: translate(-50%);
  }
}
body.curated-stay-page .theme-tab-panel {
  background: #1a1a1a;
  margin-top: 0;
  width: 100%;
}
body.curated-stay-page .theme-tab-grid {
  background: #1a1a1a;
  border-radius: var(--radius);
  display: grid;
  gap: 4px;
  grid-template-columns: repeat(2, 1fr);
  overflow: hidden;
  width: 100%;
}
body.curated-stay-page .theme-tab-card {
  aspect-ratio: 16/9;
  display: block;
  overflow: hidden;
  position: relative;
  text-decoration: none;
}
body.curated-stay-page .theme-tab-card img {
  display: block;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .theme-tab-card:hover img {
  transform: scale(1.05);
}
body.curated-stay-page .theme-tab-overlay {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0, transparent);
  bottom: 0;
  color: #fff;
  left: 0;
  padding: 16px 18px;
  position: absolute;
  right: 0;
}
body.curated-stay-page .theme-tab-option {
  font-size: 11px;
  letter-spacing: 0.1em;
  margin: 0 0 4px;
  opacity: 0.75;
  text-transform: uppercase;
}
body.curated-stay-page .theme-tab-overlay h3 {
  font-family: Spectral, serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0;
}
body.curated-stay-page .theme-stays-panel {
  background: #1a1a1a;
  margin-top: 0;
  padding-top: 32px;
}
body.curated-stay-page .theme-stays-panel .stay-list-section {
  background: #1a1a1a;
  margin-bottom: 0;
  padding-bottom: 64px;
  width: 100%;
}
body.curated-stay-page .theme-stays-panel .stay-list-title {
  color: #ffffffe6;
  font-family: Spectral, serif;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.02em;
  margin: 0 0 16px;
}
body.curated-stay-page .stay-list-desc {
  color: #ffffff80;
  font-size: 14px;
  line-height: 1.9;
  margin: -8px 0 24px;
  max-width: 700px;
}
body.curated-stay-page .theme-stays-panel .stay-list-grid {
  background: #1a1a1a;
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
}
body.curated-stay-page .theme-stays-panel .stay-list-card {
  aspect-ratio: 4/3;
  border-radius: 0;
  display: block;
  overflow: hidden;
  position: relative;
  text-decoration: none;
}
body.curated-stay-page .theme-stays-panel .stay-list-card img {
  display: block;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .theme-stays-panel .stay-list-card:hover img {
  transform: scale(1.04);
}
body.curated-stay-page .theme-stays-panel .stay-card-overlay {
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.75) 0, transparent);
  bottom: 0;
  color: #fff;
  left: 0;
  padding: 16px;
  position: absolute;
  right: 0;
}
body.curated-stay-page .theme-stays-panel .stay-card-title {
  font-family: Spectral, serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0;
}
@media (max-width: 980px) {
  body.curated-stay-page .theme-stays-panel .stay-list-section .stay-list-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  body.curated-stay-page .theme-stays-panel .stay-list-section .stay-list-grid {
    grid-template-columns: 1fr;
  }
}
body.curated-stay-page .map-body {
  align-items: stretch;
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.18);
  border-radius: var(--radius);
  display: flex;
  gap: 0;
  overflow: hidden;
}
body.curated-stay-page .map-body-left {
  background: #141414;
  border-right: 1px solid rgba(244, 241, 237, 0.15);
  flex-shrink: 0;
  padding: 20px;
  width: 36%;
}
body.curated-stay-page .map-svg-wrap {
  background: transparent;
  border: none;
  padding: 0;
}
body.curated-stay-page .jp-map {
  display: block;
  height: auto;
  width: 100%;
}
body.curated-stay-page .jp-region {
  cursor: pointer;
  fill: #2a2a2a;
  outline: none;
  stroke: #ffffff2e;
  stroke-width: 1px;
  transition: fill var(--motion-hover);
}
body.curated-stay-page .jp-region path,
body.curated-stay-page .jp-region polygon {
  fill: inherit;
  pointer-events: visiblePainted;
  stroke: inherit;
}
body.curated-stay-page .jp-region:hover {
  fill: #83632480;
}
body.curated-stay-page .jp-region.is-active {
  fill: #836324;
}
body.curated-stay-page .map-body-right {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: 24px 32px;
}
body.curated-stay-page .map-default-label {
  color: #c6a86c;
  font-size: 11px;
  letter-spacing: 0.12em;
  margin-bottom: 14px;
  text-transform: uppercase;
}
body.curated-stay-page .map-default-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin-bottom: 16px;
}
body.curated-stay-page .map-default-desc {
  color: #ffffff61;
  font-size: 14px;
  line-height: 1.9;
}
body.curated-stay-page .map-area-info {
  display: flex;
  flex-direction: column;
  gap: 0;
}
body.curated-stay-page .map-area-img-wrap {
  aspect-ratio: 16/6;
  border-radius: var(--radius-modal);
  margin-bottom: 16px;
  overflow: hidden;
  position: relative;
  width: 100%;
}
body.curated-stay-page .map-area-img-wrap:after {
  background: #00000022;
  border-radius: inherit;
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
}
body.curated-stay-page .map-area-img-wrap img {
  display: block;
  filter: brightness(1);
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
}
@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
body.curated-stay-page .map-area-region {
  color: #c6a86c;
  font-size: 10px;
  letter-spacing: 0.14em;
  margin-bottom: 10px;
  text-transform: uppercase;
}
body.curated-stay-page .map-area-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin-bottom: 16px;
}
body.curated-stay-page .map-area-desc {
  color: #ffffff8c;
  font-size: 14px;
  line-height: 1.9;
  margin-bottom: 20px;
}
body.curated-stay-page .map-area-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
body.curated-stay-page .map-area-tag {
  border: 1px solid hsla(40, 44%, 60%, 0.3);
  border-radius: var(--radius-btn);
  color: #ffffff8c;
  font-size: 11px;
  padding: 5px 14px;
}
body.curated-stay-page .map-stays-section {
  margin-top: 40px;
}
body.curated-stay-page .map-stays-label {
  color: #ffffff59;
  font-size: 11px;
  letter-spacing: 0.1em;
  margin-bottom: 16px;
  text-transform: uppercase;
}
body.curated-stay-page .map-stays-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, 1fr);
}
body.curated-stay-page .map-stay-card {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.15);
  border-radius: var(--radius);
  display: block;
  overflow: hidden;
  text-decoration: none;
  transition: border-color var(--motion-hover);
}
body.curated-stay-page .map-stay-card:hover {
  border-color: #c6a86c73;
}
body.curated-stay-page .map-stay-card img {
  aspect-ratio: 3/2;
  display: block;
  -o-object-fit: cover;
  object-fit: cover;
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .map-stay-card:hover img {
  transform: scale(1.05);
}
body.curated-stay-page .map-stay-card-body {
  padding: 10px 12px 12px;
}
body.curated-stay-page .map-stay-card-name {
  color: #ffffffd1;
  font-family: Spectral, serif;
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 3px;
}
body.curated-stay-page .map-stay-card-loc {
  color: #ffffff61;
  font-size: 10px;
}
body.curated-stay-page .map-stays-empty {
  color: #ffffff59;
  font-size: 14px;
  padding: 24px 0;
}
@media (max-width: 980px) {
  body.curated-stay-page .map-body {
    flex-direction: column;
  }
  body.curated-stay-page .map-body-left {
    border-bottom: 1px solid hsla(40, 44%, 60%, 0.15);
    border-right: none;
    width: 100%;
  }
  body.curated-stay-page .map-stays-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  body.curated-stay-page .map-stays-grid {
    grid-template-columns: 1fr;
  }
}
@media (prefers-reduced-motion: reduce) {
  body.curated-stay-page .map-stay-card img {
    transition: none;
  }
}
body.curated-stay-page .iconic-stays-section {
  padding-bottom: 0;
  padding-top: 120px;
}
body.curated-stay-page .iconic-stays-section + .map-interaction-section {
  margin-top: 0;
}
body.curated-stay-page .iconic-stays-head {
  margin-bottom: 56px;
  max-width: 640px;
}
body.curated-stay-page .iconic-stays-label {
  color: #c6a86c;
  font-size: 11px;
  letter-spacing: 0.14em;
  margin-bottom: 14px;
  text-transform: uppercase;
}
body.curated-stay-page .iconic-stays-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin-bottom: 18px;
}
body.curated-stay-page .iconic-stays-head .iconic-stays-desc {
  color: var(--muted);
  font-size: var(--text-p1-size);
  font-weight: var(--text-p1-weight);
  line-height: var(--text-p1-lh);
}
body.curated-stay-page .iconic-stays-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);
}
body.curated-stay-page .iconic-stay-card {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  transition: border-color var(--motion-hover);
}
body.curated-stay-page .iconic-stay-card:hover {
  border-color: #c6a86c66;
}
/* カード自身の transition:border-color が .reveal の transition を上書きして瞬間表示になるため、
   reveal 時は opacity/transform（stagger delay 付き）と border-color を明示的に併記して他セクションと同じフェードにする。 */
body.curated-stay-page .iconic-stay-card.reveal {
  transition:
    opacity 0.9s ease-out var(--reveal-delay, 0s),
    transform 0.9s ease-out var(--reveal-delay, 0s),
    border-color var(--motion-hover);
}
body.curated-stay-page .iconic-stay-media {
  aspect-ratio: 16/9;
  overflow: hidden;
  width: 100%;
}
body.curated-stay-page .iconic-stay-media img {
  display: block;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transition: transform var(--motion-transition);
  width: 100%;
}
body.curated-stay-page .iconic-stay-card:hover .iconic-stay-media img {
  transform: scale(1.04);
}
body.curated-stay-page .iconic-stay-body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  padding: 28px 28px 32px;
}
body.curated-stay-page .iconic-stay-location {
  color: #c6a86c;
  font-size: 11px;
  letter-spacing: 0.1em;
  margin: 0;
  text-transform: uppercase;
}
body.curated-stay-page .iconic-stay-name {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h3-size);
  font-weight: var(--text-h3-weight);
  letter-spacing: var(--text-h3-ls);
  line-height: var(--text-h3-lh);
  margin: 0;
}
body.curated-stay-page .iconic-stay-body .iconic-stay-desc {
  color: var(--muted);
  flex: 1;
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 0;
  /* CMS の overviewV2 は長さがまちまちなため、行数を固定してカード高さを揃える */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
}
body.curated-stay-page .iconic-stay-cta {
  color: #c6a86cd9;
  font-size: 13px;
  letter-spacing: 0.04em;
  margin-top: 8px;
}
body.curated-stay-page .iconic-stay-card:hover .iconic-stay-cta {
  color: #c6a86c;
}
@media (max-width: 768px) {
  body.curated-stay-page .iconic-stays-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }
}
body.curated-stay-page .gallery-section {
  --gallery-gap: 4px;
  --gallery-visible: 2.5;
  --gallery-nav-size: 48px;
  --gallery-nav-gap: 20px;
  background: #1a1a1a;
  margin-bottom: 0;
  margin-top: 0;
  overflow-x: visible;
  padding: 100px 0;
}
body.curated-stay-page .gallery-head {
  margin-bottom: 48px;
}
body.curated-stay-page .gallery-label {
  color: #c6a86c;
  font-size: 11px;
  letter-spacing: 0.14em;
  margin: 0 0 14px;
  text-transform: uppercase;
}
body.curated-stay-page .gallery-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin: 0;
}
body.curated-stay-page .gallery-slider {
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
  max-width: var(--content-max);
  position: relative;
  width: min(var(--content-max), calc(100vw - var(--page-gutter) * 2));
}
body.curated-stay-page .gallery-viewport {
  overflow: hidden;
  position: relative;
  width: 100%;
}
body.curated-stay-page .gallery-track {
  display: flex;
  gap: 4px;
  transition: transform var(--motion-transition);
  will-change: transform;
}
body.curated-stay-page .gallery-slide {
  background: var(--bg-card);
  flex: 0 0 calc((min(var(--content-max), 100vw - 2 * var(--page-gutter)) - (2 * var(--gallery-gap))) / var(--gallery-visible));
  margin: 0;
  overflow: hidden;
}
body.curated-stay-page .gallery-slide img {
  aspect-ratio: 3/2;
  display: block;
  height: auto;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
}
body.curated-stay-page .gallery-slide__link {
  display: block;
}
body.curated-stay-page .gallery-slide__link img {
  transition: opacity 0.2s ease;
}
body.curated-stay-page .gallery-slide__link:hover img {
  opacity: 0.85;
}
body.curated-stay-page .gallery-nav {
  align-items: center;
  background: transparent;
  border: none;
  color: #c6a86c;
  cursor: pointer;
  display: inline-flex;
  height: var(--gallery-nav-size);
  justify-content: center;
  padding: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition:
    color var(--motion-hover),
    background var(--motion-hover);
  width: var(--gallery-nav-size);
  z-index: 2;
}
body.curated-stay-page .gallery-nav--prev {
  left: auto;
  right: calc(100% + var(--gallery-nav-gap));
}
body.curated-stay-page .gallery-nav--next {
  left: calc(100% + var(--gallery-nav-gap));
  right: auto;
}
body.curated-stay-page .gallery-nav:hover {
  background: #242424;
  color: #d4b87a;
}
body.curated-stay-page .gallery-nav:disabled {
  cursor: default;
  opacity: 0.35;
  pointer-events: none;
}
body.curated-stay-page .gallery-nav svg {
  display: block;
  height: 18px;
  width: 18px;
}
@media (max-width: 1100px) {
  body.curated-stay-page .gallery-section {
    --gallery-visible: 2.1;
  }
}
@media (max-width: 600px) {
  body.curated-stay-page .gallery-section {
    --gallery-visible: 1.12;
    --gallery-nav-size: 40px;
    --gallery-nav-gap: 10px;
  }
  body.curated-stay-page .gallery-viewport {
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  body.curated-stay-page .gallery-viewport::-webkit-scrollbar {
    display: none;
  }
  body.curated-stay-page .gallery-track {
    transition: none;
  }
  body.curated-stay-page .gallery-slide {
    scroll-snap-align: start;
  }
  body.curated-stay-page .gallery-nav {
    display: none;
  }
}
body.curated-stay-page .gallery-footer {
  display: flex;
  justify-content: center;
  margin-top: 48px;
}
body.curated-stay-page .gallery-view-more-btn {
  align-items: center;
  background: transparent;
  border: 1px solid hsla(40, 44%, 60%, 0.45);
  border-radius: var(--radius-btn);
  color: #ffffffb8;
  cursor: pointer;
  display: inline-flex;
  font-family: Spectral, serif;
  font-size: 14px;
  justify-content: center;
  letter-spacing: 0.04em;
  padding: 14px 28px;
  transition:
    border-color var(--motion-hover),
    color var(--motion-hover);
  width: 280px;
}
body.curated-stay-page .gallery-view-more-btn:hover {
  border-color: #c6a86ce6;
  color: #fffffff2;
}
body.curated-stay-page .gallery-modal {
  align-items: center;
  background: #000000c7;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: 0;
  padding: var(--page-gutter);
  pointer-events: none;
  position: fixed;
  transition:
    opacity var(--motion-transition),
    visibility var(--motion-transition);
  visibility: hidden;
  z-index: 9999;
}
body.curated-stay-page .gallery-modal.is-open {
  opacity: 1;
  pointer-events: auto;
  visibility: visible;
}
body.curated-stay-page.gallery-modal-open {
  overflow: hidden;
}
body.curated-stay-page .gallery-modal__panel {
  background: #1a1a1a;
  border: 1px solid hsla(40, 44%, 60%, 0.28);
  max-height: min(90vh, 900px);
  overflow-y: auto;
  padding: 36px 32px 40px;
  width: min(var(--content-max), 100%);
  -webkit-overflow-scrolling: touch;
}
body.curated-stay-page .gallery-modal__head {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 28px;
}
body.curated-stay-page .gallery-modal__title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin: 0;
}
body.curated-stay-page .gallery-modal__close {
  align-items: center;
  background: transparent;
  border: 1px solid hsla(40, 44%, 60%, 0.45);
  border-radius: var(--radius-btn);
  color: #fffc;
  cursor: pointer;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 22px;
  height: 40px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  transition:
    border-color var(--motion-hover),
    color var(--motion-hover);
  width: 40px;
}
body.curated-stay-page .gallery-modal__close:hover {
  border-color: #c6a86ce6;
  color: #fff;
}
body.curated-stay-page .gallery-modal__grid {
  background: #1a1a1a;
  display: grid;
  gap: 2px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
body.curated-stay-page .gallery-modal__item {
  background: #1a1a1a;
  border: none;
  font-size: 0;
  line-height: 0;
  margin: 0;
  overflow: hidden;
  padding: 0;
}
body.curated-stay-page .gallery-modal__item img {
  aspect-ratio: 3/2;
  display: block;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
}
body.curated-stay-page .gallery-modal__link {
  display: block;
}
body.curated-stay-page .gallery-modal__link img {
  transition: opacity 0.2s ease;
}
body.curated-stay-page .gallery-modal__link:hover img {
  opacity: 0.85;
}
@media (max-width: 820px) {
  body.curated-stay-page .gallery-modal__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  body.curated-stay-page .gallery-modal__panel {
    padding: 28px 20px 32px;
  }
}
@media (max-width: 520px) {
  body.curated-stay-page .gallery-modal__grid {
    grid-template-columns: 1fr;
  }
}
body.curated-stay-page .gallery-section + .story-section {
  margin-top: 0;
}
body.curated-stay-page .story-section {
  margin-top: 0;
  padding-bottom: 96px;
  padding-top: 0;
}
body.curated-stay-page .story-head {
  align-items: flex-start;
  display: flex;
  gap: 40px;
  justify-content: space-between;
  margin-bottom: 48px;
}
body.curated-stay-page .story-head-left {
  flex-shrink: 0;
}
body.curated-stay-page .story-head-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin: 0 0 8px;
}
body.curated-stay-page .story-head-sub {
  color: #fff6;
  font-size: 13px;
  margin: 0;
}
body.curated-stay-page .story-head-desc {
  color: var(--muted);
  font-size: var(--text-p1-size);
  font-weight: var(--text-p1-weight);
  line-height: var(--text-p1-lh);
  margin: 0;
  max-width: 480px;
}
body.curated-stay-page .story-grid {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(4, 1fr);
}
body.curated-stay-page .story-card {
  background: var(--bg-card);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  transition: opacity var(--motion-hover);
}
body.curated-stay-page .story-card:hover {
  opacity: 0.82;
}
body.curated-stay-page .story-card-media {
  aspect-ratio: 3/4;
  flex-shrink: 0;
  overflow: hidden;
  width: 100%;
}
body.curated-stay-page .story-card-media img {
  display: block;
  height: 100%;
  -o-object-fit: cover;
  object-fit: cover;
  transition: transform var(--motion-transition);
  width: 100%;
}
@media (hover: hover) {
  body.curated-stay-page .story-card:hover .story-card-media img {
    transform: scale(1.04);
  }
}
body.curated-stay-page .story-card-body {
  flex: 1;
  padding: 16px 12px 20px;
}
body.curated-stay-page .story-card-label {
  color: #ffffff61;
  font-size: 11px;
  letter-spacing: 0.08em;
  line-height: 1.5;
  margin: 0 0 8px;
}
body.curated-stay-page .story-card-title {
  color: var(--cream);
  font-family: Spectral, serif;
  font-size: var(--text-p2-size);
  font-weight: var(--text-p2-weight);
  line-height: var(--text-p2-lh);
  margin: 0;
}
body.curated-stay-page .story-footer {
  display: flex;
  justify-content: center;
  margin-top: 40px;
}
body.curated-stay-page .story-find-btn {
  align-items: center;
  border: 1px solid hsla(40, 44%, 60%, 0.45);
  border-radius: var(--radius-btn);
  color: #ffffffb8;
  display: inline-flex;
  font-family: Spectral, serif;
  font-size: 14px;
  justify-content: center;
  letter-spacing: 0.04em;
  padding: 14px 28px;
  text-decoration: none;
  transition:
    border-color var(--motion-hover),
    color var(--motion-hover);
  width: 280px;
}
body.curated-stay-page .story-find-btn:hover {
  border-color: #c6a86ce6;
  color: #fffffff2;
}
@media (max-width: 980px) {
  body.curated-stay-page .story-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  body.curated-stay-page .story-head {
    flex-direction: column;
    gap: 16px;
  }
}
@media (max-width: 600px) {
  body.curated-stay-page .story-grid {
    grid-template-columns: 1fr;
  }
}
body.curated-stay-page .reviews-section {
  background: transparent;
  padding: 96px 0;
}
body.curated-stay-page .reviews-section::before {
  content: '';
  position: absolute;
  inset: 0;
  left: -100vw;
  right: -100vw;
  background: #f5f0e8;
  z-index: -1;
}
body.curated-stay-page .reviews-head {
  margin-bottom: 56px;
  text-align: center;
}
body.curated-stay-page .reviews-title {
  color: #836324;
  font-family: Spectral, serif;
  font-size: var(--text-h2-size);
  font-weight: var(--text-h2-weight);
  letter-spacing: var(--text-h2-ls);
  line-height: var(--text-h2-lh);
  margin: 0 0 14px;
}
body.curated-stay-page .reviews-subtitle {
  color: #7a6a55;
  font-size: 14px;
  letter-spacing: 0.06em;
  margin: 0;
}
body.curated-stay-page .reviews-grid {
  display: grid;
  gap: 24px 32px;
  grid-template-columns: repeat(3, 1fr);
}
body.curated-stay-page .review-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
body.curated-stay-page .review-card-top {
  align-items: flex-start;
  display: flex;
  gap: 14px;
}
body.curated-stay-page .review-img {
  border-radius: 4px;
  flex-shrink: 0;
  height: 72px;
  -o-object-fit: cover;
  object-fit: cover;
  width: 72px;
}
body.curated-stay-page .review-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 2px;
}
body.curated-stay-page .review-country {
  color: #7a6a55;
  font-size: 13px;
  margin: 0;
}
body.curated-stay-page .review-experience {
  color: var(--gold);
  font-size: 13px;
  font-weight: var(--text-p2-weight);
  letter-spacing: 0.02em;
  margin: 0;
}
body.curated-stay-page .review-text {
  color: #4a3f35;
  font-size: var(--text-p2-size);
  line-height: 1.85;
  margin: 0;
}
body.curated-stay-page .review-author {
  color: #9a8a78;
  font-size: 12px;
  letter-spacing: 0.08em;
  margin: 0;
  text-align: right;
}
@media (max-width: 980px) {
  body.curated-stay-page .reviews-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 600px) {
  body.curated-stay-page .reviews-grid {
    grid-template-columns: 1fr;
  }
}

/* Iconic Stay カード — 滞在期間・価格メタ情報 */
body.curated-stay-page .iconic-stay-meta {
  color: rgba(244, 241, 237, 0.55);
  font-size: 13px;
  letter-spacing: 0.04em;
  margin: 8px 0 12px;
}

/* テーマカードのタイトルフォントサイズ */
body.curated-stay-page .theme-tab-overlay h3 {
  font-size: 17px !important;
  line-height: 1.45 !important;
}

@media (max-width: 768px) {
  body.curated-stay-page .theme-tab-overlay {
    padding: 10px 10px 12px !important;
  }

  body.curated-stay-page .theme-tab-overlay h3 {
    font-size: 13px !important;
    line-height: 1.4 !important;
  }
}

/* テーマパネル ローディング */
body.curated-stay-page .stay-list-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: rgba(244, 241, 237, 0.5);
  font-size: 14px;
  letter-spacing: 0.12em;
}

@keyframes loadingPulse {
  0%,
  100% {
    opacity: 0.4;
  }

  50% {
    opacity: 1;
  }
}

body.curated-stay-page .loading-dots {
  animation: loadingPulse 1.4s ease-in-out infinite;
}

/* 地図シティグロウアニメーション */
@keyframes mapCityGlow {
  0% {
    fill: #2a2a2a;
  }

  40%,
  60% {
    fill: #836324;
  }

  100% {
    fill: #2a2a2a;
  }
}

body.curated-stay-page .jp-region.is-map-glow {
  animation: mapCityGlow 2s ease-in-out;
}

/* 体験なしリージョン（北海道・九州）を非活性グレーアウト */
body.curated-stay-page .jp-region.is-disabled {
  fill: #3a3a3a !important;
  opacity: 0.5;
  pointer-events: none !important;
  cursor: default;
}

body.curated-stay-page .jp-region.is-disabled path,
body.curated-stay-page .jp-region.is-disabled polygon {
  fill: inherit !important;
}

/* 初期表示コラージュ（6エリア） */
body.curated-stay-page .map-default-hero {
  width: 100%;
  aspect-ratio: 16/7;
  overflow: hidden;
  border-radius: var(--radius-modal);
  margin-bottom: 24px;
}

body.curated-stay-page .map-default-hero img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

body.curated-stay-page .map-collage {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 18px;
}

body.curated-stay-page .map-collage-item {
  position: relative;
  overflow: hidden;
  aspect-ratio: 16/7;
}

body.curated-stay-page .map-collage-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  filter: brightness(0.82) saturate(0.85);
}

body.curated-stay-page .map-collage-item::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 10, 5, 0.18);
  pointer-events: none;
}

body.curated-stay-page .map-collage-item--bright img {
  filter: brightness(1) saturate(0.85);
}

body.curated-stay-page .map-collage-item--bright::after {
  background: rgba(15, 10, 5, 0.05);
}

body.curated-stay-page .map-collage-label {
  position: absolute;
  bottom: 6px;
  left: 8px;
  color: #f4f1ed;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.8);
  pointer-events: none;
}

/* ExperienceCard 3-column grid in map section */
/* 親 .map-stays-grid が repeat(4,1fr) のため grid-column で全幅にスパン */
body.curated-stay-page .map-stays-list {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  width: 100%;
}

@media (max-width: 768px) {
  body.curated-stay-page .map-stays-list {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  body.curated-stay-page .map-stays-list {
    grid-template-columns: 1fr;
  }
}

/* エリア切り替え時にカード一覧の先頭が見えるようにマップパネルを高さ制約 (PC only) */
@media (min-width: 769px) {
  body.curated-stay-page .map-body {
    max-height: calc(100vh - 220px);
    overflow: hidden;
  }

  body.curated-stay-page .map-body-left {
    overflow: hidden;
  }

  body.curated-stay-page .map-body-right {
    overflow-y: auto;
    scrollbar-width: none;
    padding: 20px 28px !important;
    justify-content: flex-start !important;
  }
  body.curated-stay-page .map-body-right::-webkit-scrollbar {
    display: none;
  }

  /* エリア画像を短くしてテキスト + カードが見える余白を確保 */
  body.curated-stay-page .map-area-img-wrap {
    aspect-ratio: 16 / 5 !important;
    margin-bottom: 12px !important;
  }

  body.curated-stay-page .map-area-title {
    margin-bottom: 8px !important;
  }

  body.curated-stay-page .map-area-desc {
    margin-bottom: 14px !important;
  }
}

/* View More button */
body.curated-stay-page .map-stays-view-more {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  margin-top: 32px;
}

body.curated-stay-page .map-stays-view-more-btn {
  background: transparent;
  border: 1px solid hsla(40, 44%, 60%, 0.45);
  border-radius: var(--radius-btn);
  color: rgba(244, 241, 237, 0.7);
  cursor: pointer;
  font-family: Spectral, serif;
  font-size: 14px;
  letter-spacing: 0.04em;
  padding: 14px 64px;
  transition:
    border-color var(--motion-hover),
    color var(--motion-hover);
}

body.curated-stay-page .map-stays-view-more-btn:hover {
  border-color: var(--gold);
  color: var(--cream);
}

/* テーマパネル内の map-stays-section は上余白不要 */
body.curated-stay-page .theme-map-stays-section {
  margin-top: 0;
}

/* Story head — tighten gap between title and subtitle, remove space-between */
body.curated-stay-page .story-head-title {
  line-height: 1.15 !important;
  margin: 0 0 4px !important;
}

body.curated-stay-page .story-head {
  justify-content: flex-start !important;
  gap: 32px !important;
}

/* Story card — overlay style matching Wabunka top EnStoryCard */
body.curated-stay-page .story-card-media {
  position: relative;
}

body.curated-stay-page .story-card-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
}

@media (hover: hover) {
  body.curated-stay-page .story-card:hover .story-card-media img {
    transform: scale(1.05);
  }
}

body.curated-stay-page .story-card-grad {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.75));
  pointer-events: none;
}

body.curated-stay-page .story-card-body {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 14px;
}

/* Curated Themes タブ: 選択中カードのハイライト */
body.curated-stay-page .theme-tab-card.is-active::after {
  content: '';
  position: absolute;
  inset: 0;
  border: 2px solid var(--gold);
  pointer-events: none;
  z-index: 2;
}

body.curated-stay-page .theme-tab-card.is-active .theme-tab-overlay {
  background: rgba(0, 0, 0, 0.55);
}

body.curated-stay-page .theme-tab-card.is-active .theme-tab-option {
  color: var(--gold);
}

/* 静的カード（map-stay-card）のテキストサイズを ExperienceCard に合わせる */
body.curated-stay-page .map-stay-card-name {
  font-size: 16px !important;
  line-height: 1.5 !important;
}

body.curated-stay-page .map-stay-card-loc {
  font-size: 13px !important;
}

/* ExperienceCard に LP カードデザインを適用 */
body.curated-stay-page .map-stay-card-wrapper {
  border-radius: var(--radius);
  overflow: hidden;
}

body.curated-stay-page .lp-curated-stay .cta.section {
  margin-bottom: 0;
}

body.curated-stay-page .map-stays-list a.block {
  background: var(--bg-card);
  border: 1px solid rgba(244, 241, 237, 0.15);
  border-radius: var(--radius);
  overflow: hidden;
  text-decoration: none;
  transition: border-color var(--motion-hover);
  display: block;
}

body.curated-stay-page .map-stays-list a.block:hover {
  border-color: #c6a86c73;
}

/* カード高さを揃える: wrapper → card → image/text flex column */
body.curated-stay-page .map-stay-card-wrapper {
  height: 100%;
}

body.curated-stay-page .map-stays-list a.block {
  display: flex !important;
  flex-direction: column;
  height: 100%;
}

/* 画像コンテナ: 固定アスペクト比で全カードの画像高さを統一 */
body.curated-stay-page .map-stays-list a.block .overflow-hidden {
  aspect-ratio: 4 / 3;
  flex-shrink: 0;
  margin-bottom: 0;
}

/* テキストエリアが残りスペースを埋める */
body.curated-stay-page .map-stays-list a.block .overflow-hidden + div {
  flex: 1;
  padding: 8px 10px 12px;
}

/* ExperienceCard のテーマタグ行を非表示にする。
   このLPのカードデザインにテーマタグは無い。行の <div> は overflow-hidden を持つため、
   画像用の aspect-ratio 4/3 ルールに巻き込まれてカードが縦に崩れ、
   タグ自体もグローバルの p { font-size:16px } に拾われて巨大化するため、行ごと消す。 */
body.curated-stay-page .map-stays-list a.block .px-1 > div:first-child {
  display: none;
}

/* ExperienceCard タイトルを他カードと同等サイズに。
   直下の <p> はタイトルのみ（meta の <p> は入れ子）なので :first-child に依存しない。 */
body.curated-stay-page .map-stays-list a.block .px-1 > p {
  font-size: 15px !important;
  line-height: 1.5 !important;
}

/* destination | duration | price の行が折り返さないよう制御 */
body.curated-stay-page .map-stays-list a.block .text-sm,
body.curated-stay-page .map-stays-list a.block .text-base {
  font-size: 13px !important;
}

body.curated-stay-page .map-stays-list a.block .flex.flex-wrap {
  flex-wrap: nowrap !important;
}

body.curated-stay-page .map-stays-list a.block .flex.flex-wrap > *,
body.curated-stay-page .map-stays-list a.block .flex.flex-wrap span {
  white-space: nowrap;
  font-size: 13px !important;
}

body.curated-stay-page .map-stays-list a.block .flex.flex-wrap .px-1 {
  padding-left: 2px !important;
  padding-right: 2px !important;
}

body.curated-stay-page .map-stays-list a.block .flex.flex-wrap img {
  width: 10px !important;
  height: 10px !important;
}

/* 画像比率を LP デザインに合わせる（アイコン .my-auto は除外） */
body.curated-stay-page .map-stays-list img:not(.my-auto) {
  aspect-ratio: 3 / 2;
  object-fit: cover;
  width: 100%;
  display: block;
}

/* Hero fixed slideshow が隙間から透けないよう l-main に背景を設定。
   margin-top: 0 でヘッダーの真後ろから KV が始まるようにする（透明ヘッダー対応） */
body.curated-stay-page .l-main {
  position: relative;
  z-index: 5;
  background: #1a1a1a;
  margin-top: 0 !important;
}

/* グローバルフッターを LP ダークテーマに合わせる */
body.curated-stay-page #jsi-footer {
  position: relative;
  z-index: 5;
  background: #1a1a1a;
}

/* scroll-to-top バー */
body.curated-stay-page #jsi-footer .bg-black4 {
  background-color: #1a1a1a !important;
  border-top: 1px solid hsla(40, 44%, 60%, 0.2) !important;
}

body.curated-stay-page .l-footer {
  background: #1a1a1a !important;
  border-top: 1px solid hsla(40, 44%, 60%, 0.2);
  color: #f4f1ed !important;
}

body.curated-stay-page .l-footer * {
  color: #f4f1edd9 !important;
  border-color: rgba(244, 241, 237, 0.2) !important;
}

/* モバイルでリージョン選択中は map-stays-section を非表示（mob-exp-section と重複するため） */
@media (max-width: 768px) {
  body.curated-stay-page .map-stays-section.mob-region-active {
    display: none !important;
  }
}

/* ── Mobile destination card grid ── */
body.curated-stay-page .mob-dest-section {
  display: none;
}

@media (max-width: 768px) {
  /* 地図をアコーディオン制御 */
  body.curated-stay-page .map-body {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.4s ease;
  }

  body.curated-stay-page .map-body.mob-map-open {
    max-height: 2400px;
  }

  /* モバイル目的地セクションを表示 */
  body.curated-stay-page .mob-dest-section {
    display: block;
  }

  /* 区切り線 + Explore by map トグル */
  body.curated-stay-page .mob-map-accordion {
    margin-top: 20px;
  }

  body.curated-stay-page .mob-map-divider {
    border: none;
    border-top: 1px solid hsla(40, 44%, 60%, 0.2);
    margin: 0 0 0;
  }

  body.curated-stay-page .mob-map-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    padding: 14px 0;
    background: transparent;
    border: none;
    color: #c9a96e;
    font-family: Spectral, serif;
    font-size: 14px;
    letter-spacing: 0.06em;
    cursor: pointer;
    -webkit-appearance: none;
    appearance: none;
  }

  body.curated-stay-page .mob-map-chevron {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    transition: transform 0.3s ease;
  }

  body.curated-stay-page .mob-map-chevron.is-open {
    transform: rotate(180deg);
  }

  /* 2カラムカードグリッド */
  body.curated-stay-page .mob-dest-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 4px;
    border-radius: var(--radius);
    overflow: hidden;
  }

  /* 目的地カード */
  body.curated-stay-page .mob-dest-card {
    position: relative;
    aspect-ratio: 4 / 3;
    overflow: hidden;
    border: none;
    border-radius: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    outline: 2px solid transparent;
    outline-offset: -2px;
    transition: outline-color 0.15s ease;
    -webkit-appearance: none;
    appearance: none;
  }

  body.curated-stay-page .mob-dest-card.is-active {
    outline-color: #c9a96e;
  }

  body.curated-stay-page .mob-dest-media {
    position: absolute;
    inset: 0;
  }

  body.curated-stay-page .mob-dest-media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.4s ease;
  }

  body.curated-stay-page .mob-dest-card.is-active .mob-dest-media img {
    transform: scale(1.04);
  }

  body.curated-stay-page .mob-dest-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 10px 10px 12px;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.88) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
    text-align: left;
  }

  body.curated-stay-page .mob-dest-region {
    color: #e8c98a;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 3px;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  }

  body.curated-stay-page .mob-dest-title {
    color: #f4f1ed;
    font-family: Spectral, serif;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.3;
    margin: 0;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  }

  /* 体験一覧セクション */
  body.curated-stay-page .mob-exp-section {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid hsla(40, 44%, 60%, 0.2);
  }

  body.curated-stay-page .mob-exp-label {
    color: rgba(255, 255, 255, 0.45);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    margin: 0 0 16px;
  }

  body.curated-stay-page .mob-exp-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
    color: rgba(244, 241, 237, 0.5);
    font-size: 14px;
    letter-spacing: 0.12em;
  }

  body.curated-stay-page .mob-exp-empty {
    color: rgba(255, 255, 255, 0.4);
    font-size: 14px;
    padding: 32px 0;
    margin: 0;
  }

  body.curated-stay-page .mob-exp-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
  }

  /* map-stays-list を mob-exp-grid 内でも1列に */
  body.curated-stay-page .mob-exp-section .map-stays-list {
    grid-template-columns: 1fr;
  }
}

body.curated-stay-page .l-footer a:hover {
  opacity: 0.7;
}

/* スクロール前（透明ヘッダー時）はロゴを非表示 */
body.curated-stay-page .l-header:not(.is-active) .l-header__center {
  visibility: hidden;
  pointer-events: none;
}

body.curated-stay-page .l-header.header--bg-transparent:not(.is-active),
body.curated-stay-page .l-header:not(.is-active) {
  background: transparent !important;
  background-image: none !important;
}

/* ── Story section: モバイルカルーセル ── */
@media (max-width: 600px) {
  body.curated-stay-page .story-section .story-card.reveal,
  body.curated-stay-page .story-section .story-card.reveal.is-visible {
    opacity: 1;
    transform: none;
    transition: none;
  }
  body.curated-stay-page .story-section .story-grid {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 8px;
    /* 右端に余白を付けて次カードをチラ見せ */
    padding-right: 20px;
    scrollbar-width: none;
    /* 外側コンテナの padding を無効化してフル幅スクロール */
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    padding-left: calc(50vw - 50% + 16px);
  }

  body.curated-stay-page .story-section .story-grid::-webkit-scrollbar {
    display: none;
  }

  body.curated-stay-page .story-section .story-card {
    flex: 0 0 78vw;
    scroll-snap-align: start;
  }
}
</style>
